import React, { useState, useRef, useCallback, useEffect } from 'react';
import { MIN_PASSWORD_LENGTH, PASSWORD_HINT } from '../lib/passwordRule';
import { createPortal } from 'react-dom';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import {
  User, MapPin, Phone, Shield, Briefcase, Calendar, Clock,
  ChevronDown, ChevronUp, Plus, Trash2, Upload, X, Loader2,
  ChevronsUpDown, ChevronsDownUp, Eye, EyeOff, CheckCircle, XCircle, UserCog, Info, Banknote
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
// ⚠️ ONE county list — this file used to carry its own, and three counties were spelled
// differently from the Locations one (Durham / County Durham, Orkney Islands / Orkney,
// Shetland Islands / Shetland). Same person, two screens, two spellings.
import { UK_COUNTIES } from '../lib/counties';
import { HoverCard, HoverCardContent, HoverCardTrigger } from './ui/hover-card';
import { Checkbox } from './ui/checkbox';
import { Switch } from './ui/switch';
import { usersApi, settingsApi, tagsApi, dashboardApi, entitlementApi, approvalPoliciesApi, holidaysApi } from '../lib/api';
import { formatLeave } from '../lib/leaveUnits';
import { statutoryBox } from '../lib/statutoryBox';
import { toast } from 'sonner';
import AddressLookup from './AddressLookup';
import WorkingHoursEditor, { computeWeeklyHours } from './WorkingHoursEditor';
// ⚠️ Pure, and CHECKED — `node src/lib/workingDays.checks.mjs`. `inheritedWeek` mirrors
// the lower rungs of the backend's `effective_pattern` so this editor can SAY what an
// employee follows instead of drawing it as if they had chosen it.
import { describeWorkingDays, inheritedFrom, inheritedWeek } from '../lib/workingDays';
// ⚠️ Pure, and pinned against its Python twin by `backend/tests/test_pro_rata_agrees.py`.
import AllowanceProRataNote from './AllowanceProRataNote';
import { daysAWeekIn } from '../lib/proRata';
import { DAY_KEYS } from '../lib/workingWeek';

/**
 * The plain-English "who does this cover" line under the payroll toggle.
 *
 * ⚠️ GENERATED, NEVER TYPED (DESIGN-payroll.md). Letting an ordinary employee
 * export the company's absence data is only safe because whoever grants it is
 * told, at that moment, exactly whose data it is.
 *
 * A function declaration at module scope on purpose: it is called from deep in
 * the JSX below, and a `const` arrow read above its own declaration parses
 * perfectly and throws the instant the dialog opens (CLAUDE.md rule 11).
 */
function payrollCoversText(form, departments = [], locations = [], people = []) {
  const scope = form?.payroll_scope || 'none';
  if (scope === 'company') return 'everyone in the company';
  if (scope === 'inherit') return 'everyone they can already see';
  if (scope === 'departments') {
    const names = (departments || [])
      .filter((d) => (form.payroll_scope_department_ids || []).includes(d.id))
      .map((d) => d.name);
    return names.length ? names.join(', ') : 'no departments yet — pick at least one';
  }
  if (scope === 'locations') {
    const names = (locations || [])
      .filter((l) => (form.payroll_scope_location_ids || []).includes(l.id))
      .map((l) => l.name);
    return names.length ? names.join(', ') : 'no locations yet — pick at least one';
  }
  if (scope === 'people') {
    const names = (people || [])
      .filter((p) => (form.payroll_scope_user_ids || []).includes(p.id))
      .map((p) => `${p.first_name || ''} ${p.last_name || ''}`.trim() || p.email);
    return names.length ? names.join(', ') : 'nobody chosen yet — pick at least one';
  }
  return 'nobody yet';
}

// Small hover info tooltip
const InfoTip = ({ text }) => (
  <HoverCard openDelay={120} closeDelay={80}>
    <HoverCardTrigger asChild>
      <button type="button" tabIndex={-1} onClick={(e) => e.preventDefault()} className="text-muted-foreground/50 hover:text-muted-foreground align-middle">
        <Info className="w-3.5 h-3.5" />
      </button>
    </HoverCardTrigger>
    <HoverCardContent align="start" className="w-64 p-3 text-xs leading-relaxed text-muted-foreground">{text}</HoverCardContent>
  </HoverCard>
);

// UK Counties list

// Collapsible Section Component
const CollapsibleSection = ({ title, icon: Icon, children, isOpen, onToggle }) => {
  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        data-section-toggle="true"
        className="w-full flex items-center justify-between p-4 bg-secondary/30 hover:bg-secondary/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center">
            <Icon className="w-4 h-4 text-primary" />
          </div>
          <span className="font-medium">{title}</span>
        </div>
        {isOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
      </button>
      {isOpen && (
        <div className="p-4 space-y-4 bg-card">
          {children}
        </div>
      )}
    </div>
  );
};

// Profile Picture Cropper Component
const ProfilePictureCropper = ({ currentImage, onImageChange }) => {
  const [imgSrc, setImgSrc] = useState('');
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState();
  const [showCropper, setShowCropper] = useState(false);
  const imgRef = useRef(null);
  const inputRef = useRef(null);

  const onSelectFile = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        setImgSrc(reader.result?.toString() || '');
        setShowCropper(true);
      });
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  const onImageLoad = useCallback((e) => {
    const { width, height } = e.currentTarget;
    const size = Math.min(width, height);
    const x = (width - size) / 2;
    const y = (height - size) / 2;
    setCrop({
      unit: 'px',
      x,
      y,
      width: size,
      height: size,
    });
  }, []);

  const getCroppedImg = async () => {
    if (!imgRef.current || !completedCrop) return;
    
    const canvas = document.createElement('canvas');
    const scaleX = imgRef.current.naturalWidth / imgRef.current.width;
    const scaleY = imgRef.current.naturalHeight / imgRef.current.height;
    canvas.width = 200;
    canvas.height = 200;
    const ctx = canvas.getContext('2d');
    
    ctx.drawImage(
      imgRef.current,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      200,
      200
    );
    
    const base64Image = canvas.toDataURL('image/jpeg', 0.9);
    onImageChange(base64Image);
    setShowCropper(false);
    setImgSrc('');
  };

  return (
    <div className="flex flex-col items-center gap-4">
      {/* Current Image or Placeholder */}
      <div 
        className="w-24 h-24 rounded-full bg-secondary flex items-center justify-center overflow-hidden border-2 border-border cursor-pointer hover:border-primary transition-colors"
        onClick={() => inputRef.current?.click()}
      >
        {currentImage ? (
          <img src={currentImage} alt="Profile" className="w-full h-full object-cover" />
        ) : (
          <User className="w-10 h-10 text-muted-foreground" />
        )}
      </div>
      
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={onSelectFile}
        className="hidden"
      />
      
      <Button 
        type="button" 
        variant="outline" 
        size="sm"
        onClick={() => inputRef.current?.click()}
      >
        <Upload className="w-4 h-4 mr-2" />
        Upload Photo
      </Button>
      
      {/* Cropper Modal */}
      {showCropper && (
        <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4">
          <div className="bg-card rounded-lg max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold">Crop Image</h3>
              <button onClick={() => setShowCropper(false)}>
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="max-h-[60vh] overflow-auto mb-4">
              <ReactCrop
                crop={crop}
                onChange={(c) => setCrop(c)}
                onComplete={(c) => setCompletedCrop(c)}
                aspect={1}
                circularCrop
              >
                <img
                  ref={imgRef}
                  src={imgSrc}
                  onLoad={onImageLoad}
                  alt="Crop preview"
                  className="max-w-full"
                />
              </ReactCrop>
            </div>
            
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowCropper(false)}>
                Cancel
              </Button>
              <Button className="btn-primary" onClick={getCroppedImg}>
                Apply
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Emergency Contact Item
const EmergencyContactItem = ({ contact, index, onUpdate, onRemove, employeeAddress }) => {
  const copyEmployeeAddress = () => {
    onUpdate({
      ...contact,
      address_line_1: employeeAddress.address_line_1 || '',
      address_line_2: employeeAddress.address_line_2 || '',
      address_line_3: employeeAddress.address_line_3 || '',
      town: employeeAddress.town || '',
      county: employeeAddress.county || '',
      postcode: employeeAddress.postcode || '',
    });
  };

  return (
    <div className="p-4 bg-secondary/30 rounded-lg space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-muted-foreground">
          Contact #{index + 1} {contact.priority === 1 && '(Primary)'}
        </span>
        <Button 
          type="button" 
          variant="ghost" 
          size="sm" 
          className="text-destructive hover:text-destructive"
          onClick={onRemove}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
      
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Name *</Label>
          <Input
            value={contact.name}
            onChange={(e) => onUpdate({ ...contact, name: e.target.value })}
            className="input-hive mt-1"
            placeholder="Full name"
          />
        </div>
        <div>
          <Label className="text-xs">Relationship</Label>
          <Select 
            value={contact.relation || ''} 
            onValueChange={(v) => onUpdate({ ...contact, relation: v })}
          >
            <SelectTrigger className="input-hive mt-1">
              <SelectValue placeholder="Select..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="spouse">Spouse</SelectItem>
              <SelectItem value="partner">Partner</SelectItem>
              <SelectItem value="parent">Parent</SelectItem>
              <SelectItem value="sibling">Sibling</SelectItem>
              <SelectItem value="child">Child</SelectItem>
              <SelectItem value="friend">Friend</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Phone</Label>
          <Input
            value={contact.phone || ''}
            onChange={(e) => onUpdate({ ...contact, phone: e.target.value })}
            className="input-hive mt-1"
          />
        </div>
        <div>
          <Label className="text-xs">Mobile</Label>
          <Input
            value={contact.mobile || ''}
            onChange={(e) => onUpdate({ ...contact, mobile: e.target.value })}
            className="input-hive mt-1"
          />
        </div>
      </div>
      
      {/* Address Section with Lookup */}
      <div className="border-t border-border pt-3 mt-2">
        <div className="flex items-center justify-between mb-2">
          <Label className="text-xs font-medium">Address</Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={copyEmployeeAddress}
            className="text-xs h-7"
          >
            <MapPin className="w-3 h-3 mr-1" />
            Copy employee's address
          </Button>
        </div>
        
        {/* Address Lookup */}
        <AddressLookup
          initialPostcode={contact.postcode || ''}
          onAddressSelect={(address) => {
            onUpdate({
              ...contact,
              address_line_1: address.address_line_1,
              address_line_2: address.address_line_2,
              town: address.city,
              county: address.county,
              postcode: address.postcode,
            });
          }}
        />
        
        <div className="mt-2 space-y-2">
          <div>
            <Label className="text-xs">Address Line 1</Label>
            <Input
              value={contact.address_line_1 || ''}
              onChange={(e) => onUpdate({ ...contact, address_line_1: e.target.value })}
              className="input-hive mt-1"
              placeholder="Street address"
            />
          </div>
          
          <div>
            <Label className="text-xs">Address Line 2</Label>
            <Input
              value={contact.address_line_2 || ''}
              onChange={(e) => onUpdate({ ...contact, address_line_2: e.target.value })}
              className="input-hive mt-1"
            />
          </div>
          
          <div>
            <Label className="text-xs">Address Line 3</Label>
            <Input
              value={contact.address_line_3 || ''}
              onChange={(e) => onUpdate({ ...contact, address_line_3: e.target.value })}
              className="input-hive mt-1"
            />
          </div>
          
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Town</Label>
              <Input
                value={contact.town || ''}
                onChange={(e) => onUpdate({ ...contact, town: e.target.value })}
                className="input-hive mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">County</Label>
              <Select 
                value={contact.county || ''} 
                onValueChange={(v) => onUpdate({ ...contact, county: v })}
              >
                <SelectTrigger className="input-hive mt-1">
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent className="max-h-[200px]">
                  {UK_COUNTIES.map(county => (
                    <SelectItem key={county} value={county}>{county}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Postcode</Label>
              <Input
                value={contact.postcode || ''}
                onChange={(e) => onUpdate({ ...contact, postcode: e.target.value })}
                className="input-hive mt-1"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Dialog Component
const AddEditEmployeeDialog = ({ 
  open, 
  onOpenChange, 
  employee = null, 
  departments = [],
  people = [],
  locations = [],       // New prop for locations
  locationsEnabled = false,  // Whether locations feature is enabled
  companyDefaultAllowance = 28,  // Company default annual allowance
  onSuccess,
  viewOnly = false,  // New prop for view-only mode (managers viewing employee details)
  isAdmin = true,    // Whether the current user is an admin (controls Manager Settings visibility)
  // Which tab to land on, by key ('basics' | 'employment' | 'leave' | 'personal' |
  // 'permissions'). The org chart opens straight onto Permissions, because that's the
  // screen you want when you've just clicked someone whose approval routing looks wrong.
  // Falls back to the first tab if the key isn't available to this user.
  initialTab = null,
  // Highlight the approval fields — used when arriving from the chart's needs-attention
  // strip, so the thing that's missing is the thing your eye lands on.
  highlightApproval = false,
}) => {
  const isEditing = !!employee;
  const [loading, setLoading] = useState(false);
  // A3: "days or hours?" answered in this dialog — hides the flag until it is reopened.
  const [unitAnswered, setUnitAnswered] = useState(false);
  useEffect(() => { setUnitAnswered(false); }, [employee?.id, open]);
  const [companyHours, setCompanyHours] = useState(null);
  // Which countries have selectable regions, from the one endpoint that knows.
  const [regionsByCountry, setRegionsByCountry] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [availableTags, setAvailableTags] = useState([]);
  const [approvalSummary, setApprovalSummary] = useState(null);
  const [selectedTagIds, setSelectedTagIds] = useState([]);

  // ---------------------------------------------------------------------------
  // Who may be picked in the three approval dropdowns
  //
  // `people` arrives with include_archived: true (EmployeesPage needs leavers for its
  // own list), so without filtering, former staff show up as ordinary choices.
  //
  // Two different rules apply, and conflating them is the bug this replaced:
  //
  //   * "Reports to" is the ORG CHART. Anyone can be someone's manager on paper —
  //     in a flat company plenty of people report to a non-manager. Open list.
  //   * "Approved by" / "Escalates to" must be someone who can ACTUALLY sign off.
  //     Only manager/admin/superadmin can action a request (require_manager guards
  //     the endpoint, and the Approvals screen is role-gated too), so naming anyone
  //     else creates a request that can never be actioned.
  //
  // In both cases somebody ALREADY saved in the field is kept and tagged, rather than
  // hidden — dropping them would show a misleading placeholder while a stale id sat in
  // the record. Better the admin sees it and fixes it.
  // ---------------------------------------------------------------------------
  const APPROVING_ROLES = ['manager', 'admin', 'superadmin'];
  const canApprove = (p) => !p.is_archived && APPROVING_ROLES.includes(p.role);

  // "(not a manager)" is only a problem in the approver fields — on the org chart it's
  // ordinary, so don't flag it there or it reads as an error when nothing is wrong.
  const label = (p, flagRole) => `${p.first_name} ${p.last_name}`
    + (p.is_archived ? ' (left)'
      : (flagRole && !canApprove(p) ? ' (not a manager)'
        : (!p.email ? ' (no email)' : '')));

  // Only the "already saved" escape hatch may bypass a rule — everything else about
  // who's offered lives in the rule itself. Note `p.email` is INSIDE the rules, not
  // outside: an email-less person can't be notified so shouldn't be a fresh choice,
  // but if one is already saved (Timetastic-style staff with no work email) they must
  // still be visible, or the field shows a blank placeholder over a real stored value.
  const options = (currentValue, rule, flagRole) => people
    .filter((p) => p.id !== employee?.id && (rule(p) || p.id === currentValue))
    .map((p) => ({ ...p, label: label(p, flagRole) }));

  const managerOptions = (currentValue) => options(currentValue, (p) => p.email && !p.is_archived, false);
  const approverOptions = (currentValue) => options(currentValue, (p) => p.email && canApprove(p), true);

  // Approval groups are selectable in the same dropdowns as people — a slot holds
  // either one person or one group. Encoded as "group:<id>" so a single Select can
  // offer both without two controls; the backend keeps the either/or invariant.
  const [approvalGroups, setApprovalGroups] = useState([]);
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await approvalPoliciesApi.listApprovalGroups();
        setApprovalGroups(data || []);
      } catch {
        setApprovalGroups([]);   // groups are optional; a failure mustn't block the form
      }
    })();
  }, [open]);

  const GROUP_PREFIX = 'group:';
  const slotValue = (personId, groupId) => (groupId ? `${GROUP_PREFIX}${groupId}` : (personId || ''));
  const applySlot = (raw, personField, groupField) => {
    if (raw.startsWith(GROUP_PREFIX)) {
      setForm({ ...form, [personField]: '', [groupField]: raw.slice(GROUP_PREFIX.length) });
    } else {
      setForm({ ...form, [personField]: raw, [groupField]: '' });
    }
  };

  // NB: anything depending on `form` must be declared AFTER its useState below,
  // not here — see sameAsManagerBlocked.
  // ADVISORY rest-break warnings for this employee's pattern override, from the
  // backend (the law lives in services/jurisdictions.py, never in the frontend).
  const [hoursWarnings, setHoursWarnings] = useState([]);
  // ADVISORY statutory entitlement for this employee: their floor computed from
  // their ACTUAL schedule vs what they've been granted. null = unmodelled country
  // or new employee = show nothing (D9).
  const [entitlementInfo, setEntitlementInfo] = useState(null);
  
  // Effective-dated schedule changes: the one queued future change (if any),
  // and the save-time prompt state ("have these always been their hours, or
  // do they change from a date?") — shown only when the pattern was edited.
  const [upcomingChange, setUpcomingChange] = useState(null);
  const [schedulePrompt, setSchedulePrompt] = useState(null);   // {payload} while asking
  const [scheduleApplyMode, setScheduleApplyMode] = useState('always'); // 'always' | 'from_date'
  const [scheduleFromDate, setScheduleFromDate] = useState('');

  // Form state
  const [form, setForm] = useState({
    // Personal Info
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    must_change_password: false,  // Force password change on first login
    date_of_birth: '',
    profile_picture_url: '',
    
    // Address
    address_line_1: '',
    address_line_2: '',
    address_line_3: '',
    town: '',
    county: '',
    postcode: '',
    country: 'United Kingdom',
    
    // Contact
    home_phone: '',
    mobile_phone: '',
    work_phone: '',
    
    // Employment
    role: 'employee',
    department_id: '',
    location_id: '',     // New: location assignment
    public_holiday_region_override: '',  // '' = use location/company default
    job_title: '',
    start_date: '',
    employee_id: '',
    payroll_id: '',
    notice_period_weeks: '',
    hours_per_week: '',
    working_hours_override: '',
    
    // Leave
    annual_allowance: '',
    opening_balance_used: '',
    yearly_allowance_increment: '0',
    works_public_holidays: false,
    display_unit: 'days',

    // Manager Settings (what manager can do with THIS employee)
    manager_can_view: true,
    manager_can_edit: false,
    
    // Manager Permission Overrides (when THIS employee is a manager)
    use_global_manager_settings: true,
    mgr_override_can_view_team_balances: true,
    mgr_override_can_approve_leave: true,
    mgr_override_can_view_leave_reasons: true,
    mgr_override_can_edit_team_details: false,
    mgr_override_can_view_personal_info: false,
    mgr_override_can_cancel_approved_leave: false,
    mgr_override_can_approve_own_leave: false,
    mgr_override_can_book_team_leave: false,
    mgr_override_receive_notifications: true,
    mgr_override_max_approval_days: 0,
    mgr_override_can_create_blackouts: false,
    mgr_override_can_view_reports: false,
    mgr_override_can_view_sickness: false,
    mgr_override_view_all_departments: false,
    mgr_override_manage_all_departments: false,
    manager_view_scope: 'own',
    manager_manage_scope: 'own',
    managed_department_ids: [],

    // Payroll (DESIGN-payroll.md) — its own axis, deliberately not grouped with
    // the manager reach fields above.
    can_run_payroll: false,
    payroll_scope: 'none',
    payroll_scope_department_ids: [],
    payroll_scope_location_ids: [],
    payroll_scope_user_ids: [],

    // Approval & reporting (per-employee override)
    reports_to_id: '',
    approver_id: '',
    backup_approver_id: '',
    approver_group_id: '',
    backup_approver_group_id: '',
    approvers_final: false,
    
    // Options
    send_login_email: false,
  });

  // "Same as manager" routes to whoever is in Reports to, so it only works if that
  // person can approve. Blocked at the point of configuration rather than left to
  // dead-end at booking time. An empty Reports to is fine: routing then falls through
  // to the department manager, then an admin.
  //
  // Must live BELOW the form useState above — reading `form` any earlier throws
  // "Cannot access 'form' before initialization" and takes the whole page down.
  const reportsToPerson = people.find((p) => p.id === form.reports_to_id);
  const sameAsManagerBlocked = !!form.reports_to_id && !!reportsToPerson && !canApprove(reportsToPerson);

  const [emergencyContacts, setEmergencyContacts] = useState([]);
  
  // Collapsible sections state
  const [sections, setSections] = useState({
    personal: false,
    address: false,
    contact: false,
    emergency: false,
    employment: false,
    managerSettings: false,
    managerPermissions: false,
    approval: false,
    workingHours: false,
    leave: false,
  });
  
  const toggleSection = (section) => {
    setSections(prev => ({ ...prev, [section]: !prev[section] }));
  };
  
  const expandAll = () => {
    setSections({
      personal: true,
      address: true,
      contact: true,
      emergency: true,
      employment: true,
      managerSettings: true,
      managerPermissions: true,
      workingHours: true,
      leave: true,
    });
  };
  
  const collapseAll = () => {
    setSections({
      personal: false,
      address: false,
      contact: false,
      emergency: false,
      employment: false,
      managerSettings: false,
      managerPermissions: false,
      workingHours: false,
      leave: false,
    });
  };
  
  // Global manager settings for "View Defaults" popover
  const [globalManagerSettings, setGlobalManagerSettings] = useState(null);
  const [loadingGlobalSettings, setLoadingGlobalSettings] = useState(false);
  
  const fetchGlobalManagerSettings = async () => {
    setLoadingGlobalSettings(true);
    try {
      const res = await settingsApi.getManagerSettings();
      setGlobalManagerSettings(res.data);
    } catch (error) {
      console.error('Failed to fetch global settings:', error);
      toast.error('Failed to load global settings');
    } finally {
      setLoadingGlobalSettings(false);
    }
  };
  
  // Permission labels for display
  const permissionLabels = {
    mgr_can_view_team_balances: 'View Team Leave Balances',
    mgr_can_approve_leave: 'Approve/Decline Leave',
    mgr_can_view_leave_reasons: 'View Leave Reasons',
    mgr_can_edit_team_details: 'Edit Team Member Details',
    mgr_can_view_personal_info: 'View Personal Info',
    mgr_can_cancel_approved_leave: 'Cancel Approved Leave',
    mgr_receive_notifications: 'Email Notifications',
    mgr_max_approval_days: 'Max Days Can Approve'
  };

  // The location currently chosen in the FORM, not the one saved on the employee — the
  // admin may have just changed the dropdown, and everything below has to follow that.
  const chosenLocation = React.useMemo(
    () => (locations || []).find((b) => b.id === form.location_id) || null,
    [locations, form.location_id]);

  // What they would follow with no pattern of their own, so the editor can SAY it rather
  // than draw ticked boxes that look like a saved choice.
  // ⚠️ THE HOURS THIS PERSON AND THIS COMPANY ACTUALLY WORK, so the allowance note can ask
  // whether the two match. `computeWeeklyHours` is the same function the working-hours
  // editor uses to fill in the hours box — one answer to "how long is their week", not two.
  const theirWeeklyMinutes = React.useMemo(() => {
    const own = form.working_hours_override;
    const hours = own
      ? computeWeeklyHours(typeof own === 'string' ? JSON.parse(own) : own, companyHours)
      // No pattern of their own: they follow the location or the company, which is what
      // `inheritedWeek` resolves. Their week is that week.
      : computeWeeklyHours(inheritedWeek(chosenLocation, companyHours), companyHours);
    return hours ? Math.round(hours * 60) : null;
  }, [form.working_hours_override, chosenLocation, companyHours]);

  const companyWeeklyMinutes = React.useMemo(() => {
    const hours = computeWeeklyHours(companyHours, companyHours);
    return hours ? Math.round(hours * 60) : null;
  }, [companyHours]);

  // How long one of this company's days is — its week divided by the days it works.
  //
  // ⚠️ `daysWorkedIn` RATHER THAN COUNTING THE KEYS BY HAND. The first version of this
  // walked the pattern object inline and it was six lines of unreadable optional
  // chaining; `lib/workingWeek.js` has answered "which days does this pattern work" since
  // step 2 and is checked by `workingWeek.checks.mjs`. There is no reason for a second
  // opinion about it inside a dialog.
  // How many of this year's bank holidays fall on days THIS person works.
  //
  // ⚠️ IT IS THE LINE THAT STOPS A SMALL NUMBER LOOKING BROKEN. "4 days" on its own reads
  // as a mistake; "4 days, plus the 5 bank holidays that fall on their working days — 9
  // days off in total" reads as an arrangement. Rob's Monday-only employee is the case:
  // the honest pro-rata figure is small precisely BECAUSE the bank holidays are generous.
  //
  // ⚠️ COUNTED IN THE BROWSER, FROM THE SAME `/holidays` LIST THE REST OF THE APP READS,
  // rather than fetched from the allowance receipt — because a NEW starter has no
  // allowance yet, and the note has to work while somebody is still typing them in.
  const [holidayDates, setHolidayDates] = useState([]);
  useEffect(() => {
    if (!open) return;
    const year = new Date().getFullYear();
    holidaysApi.list(year, employee?.id)
      .then((r) => setHolidayDates((r.data || []).map((h) => h.date)))
      // A count we cannot fetch simply is not shown — `bankHolidaysLanding={null}` hides
      // the sentence rather than guessing a number onto a leave figure.
      .catch(() => setHolidayDates([]));
  }, [open, employee?.id]);

  const bankHolidaysLanding = React.useMemo(() => {
    if (!holidayDates.length) return null;
    const own = form.working_hours_override;
    const week = own
      ? (typeof own === 'string' ? JSON.parse(own) : own)
      : inheritedWeek(chosenLocation, companyHours);
    if (!week) return null;
    const worksDay = DAY_KEYS.map(
      (d) => ['am', 'pm'].some((sess) => week[d]?.[sess]?.enabled) || !!week[d]?.enabled);
    return holidayDates.filter((iso) => {
      // ⚠️ `T00:00:00` — without it a bare date is parsed as UTC and a British summer
      // date can slide to the previous day, which would move a Monday holiday to a Sunday
      // and quietly change the count.
      const dow = new Date(`${iso}T00:00:00`).getDay();
      return worksDay[(dow + 6) % 7];      // JS Sunday=0; DAY_KEYS starts on Monday
    }).length;
  }, [holidayDates, form.working_hours_override, chosenLocation, companyHours]);

  // How many days a week THIS person works — their own pattern if they have one, or the
  // week they inherit. Used to value one of their days, which is what turns "4 days" into
  // the hours somebody on the hours display actually reads.
  const theirDaysAWeek = React.useMemo(() => {
    const own = form.working_hours_override;
    const week = own || inheritedWeek(chosenLocation, companyHours);
    // ⚠️ `daysAWeekIn`, which reads BOTH pattern shapes — see lib/proRata.js.
    return daysAWeekIn(week);
  }, [form.working_hours_override, chosenLocation, companyHours]);
  const companyDaysAWeek = React.useMemo(() => daysAWeekIn(companyHours), [companyHours]);

  const companyDayMinutes = React.useMemo(() => {
    const worked = companyDaysAWeek;   // both pattern shapes — see lib/proRata.js
    return companyWeeklyMinutes && worked ? companyWeeklyMinutes / worked : null;
  }, [companyDaysAWeek, companyWeeklyMinutes]);

  const inheritedDays = React.useMemo(() => {
    const wk = inheritedWeek(chosenLocation, companyHours);
    if (!wk) return [];
    return Object.keys(wk).filter(   // both shapes — BUGS.md F89
      (d) => wk[d]?.enabled === true || ['am', 'pm'].some((sess) => wk[d]?.[sess]?.enabled));
  }, [chosenLocation, companyHours]);

  // Regions this person could legitimately be in: their LOCATION's country decides, not
  // the company's — that is the whole point of having locations abroad. If we can't tell
  // (no location chosen, or an older location with no country), fall back to the UK so the
  // control keeps working exactly as it does today for existing customers.
  const overrideRegions = React.useMemo(() => {
    const country = chosenLocation?.country_code || 'GB';
    return regionsByCountry[country] || [];
  }, [chosenLocation, regionsByCountry]);


  // Load company hours and populate form if editing
  useEffect(() => {
    if (open) {
      settingsApi.getBusinessHours().then(res => {
        setCompanyHours(res.data.business_hours);
      }).catch(() => {});
      tagsApi.list().then(r => setAvailableTags(r.data || [])).catch(() => {});
      holidaysApi.getCountries()
        .then(r => setRegionsByCountry(r.data.regions_by_country || {}))
        .catch(() => {});

      if (employee) {
        setForm({
          first_name: employee.first_name || '',
          last_name: employee.last_name || '',
          email: employee.email || '',
          password: '',
          date_of_birth: employee.date_of_birth || '',
          profile_picture_url: employee.profile_picture_url || '',
          address_line_1: employee.address_line_1 || '',
          address_line_2: employee.address_line_2 || '',
          address_line_3: employee.address_line_3 || '',
          town: employee.town || '',
          county: employee.county || '',
          postcode: employee.postcode || '',
          country: employee.country || 'United Kingdom',
          home_phone: employee.home_phone || '',
          mobile_phone: employee.mobile_phone || '',
          work_phone: employee.work_phone || '',
          role: employee.role || 'employee',
          department_id: employee.department_id || '',
          location_id: employee.location_id || '',
          public_holiday_region_override: employee.public_holiday_region_override || '',
          job_title: employee.job_title || '',
          start_date: employee.start_date || '',
          employee_id: employee.employee_id || '',
          payroll_id: employee.payroll_id || '',
          notice_period_weeks: employee.notice_period_weeks?.toString() || '',
          hours_per_week: employee.hours_per_week?.toString() || '',
          working_hours_override: employee.working_hours_override || '',
          annual_allowance: employee.annual_allowance?.toString() || '',
          opening_balance_used: '',
          yearly_allowance_increment: employee.yearly_allowance_increment?.toString() || '0',
          works_public_holidays: employee.works_public_holidays || false,
          display_unit: employee.display_unit || 'days',
          manager_can_view: employee.manager_can_view !== false, // Default true if not set
          manager_can_edit: employee.manager_can_edit || false,
          // Manager Permission Overrides
          use_global_manager_settings: employee.use_global_manager_settings !== false, // Default true
          mgr_override_can_view_team_balances: employee.mgr_override_can_view_team_balances !== false,
          mgr_override_can_approve_leave: employee.mgr_override_can_approve_leave !== false,
          mgr_override_can_view_leave_reasons: employee.mgr_override_can_view_leave_reasons !== false,
          mgr_override_can_edit_team_details: employee.mgr_override_can_edit_team_details || false,
          mgr_override_can_view_personal_info: employee.mgr_override_can_view_personal_info || false,
          mgr_override_can_cancel_approved_leave: employee.mgr_override_can_cancel_approved_leave || false,
          mgr_override_can_approve_own_leave: employee.mgr_override_can_approve_own_leave || false,
          mgr_override_can_book_team_leave: employee.mgr_override_can_book_team_leave || false,
          mgr_override_receive_notifications: employee.mgr_override_receive_notifications !== false,
          mgr_override_max_approval_days: employee.mgr_override_max_approval_days || 0,
          mgr_override_can_create_blackouts: employee.mgr_override_can_create_blackouts || false,
          mgr_override_can_view_reports: employee.mgr_override_can_view_reports || false,
          mgr_override_can_view_sickness: employee.mgr_override_can_view_sickness || false,
          mgr_override_view_all_departments: employee.mgr_override_view_all_departments || false,
          mgr_override_manage_all_departments: employee.mgr_override_manage_all_departments || false,
          manager_view_scope: employee.manager_view_scope || 'own',
          manager_manage_scope: employee.manager_manage_scope || 'own',
          managed_department_ids: employee.managed_department_ids || [],
          can_run_payroll: employee.can_run_payroll || false,
          payroll_scope: employee.payroll_scope || 'none',
          payroll_scope_department_ids: employee.payroll_scope_department_ids || [],
          payroll_scope_location_ids: employee.payroll_scope_location_ids || [],
          payroll_scope_user_ids: employee.payroll_scope_user_ids || [],
          reports_to_id: employee.reports_to_id || '',
          approver_id: employee.approver_id || '',
          backup_approver_id: employee.backup_approver_id || '',
          approver_group_id: employee.approver_group_id || '',
          backup_approver_group_id: employee.backup_approver_group_id || '',
          approvers_final: employee.approvers_final || false,
          send_login_email: false,
        });
        setEmergencyContacts(employee.emergency_contacts || []);
        setSelectedTagIds((employee.tags || []).map((t) => t.id));
        setSchedulePrompt(null);
        setUpcomingChange(null);
        usersApi.getSchedule(employee.id)
          .then(r => setUpcomingChange(r.data?.upcoming || null))
          .catch(() => setUpcomingChange(null));
      } else {
        // Reset form for new employee
        setForm({
          first_name: '', last_name: '', email: '', password: '',
          date_of_birth: '', profile_picture_url: '',
          address_line_1: '', address_line_2: '', address_line_3: '',
          town: '', county: '', postcode: '', country: 'United Kingdom',
          home_phone: '', mobile_phone: '', work_phone: '',
          role: 'employee', department_id: '', location_id: '', public_holiday_region_override: '', job_title: '',
          start_date: '', employee_id: '', payroll_id: '',
          notice_period_weeks: '', hours_per_week: '', working_hours_override: '',
          annual_allowance: '', opening_balance_used: '',
          yearly_allowance_increment: '0', works_public_holidays: false,
          display_unit: 'days',
          manager_can_view: true, manager_can_edit: false,
          use_global_manager_settings: true,
          mgr_override_can_view_team_balances: true,
          mgr_override_can_approve_leave: true,
          mgr_override_can_view_leave_reasons: true,
          mgr_override_can_edit_team_details: false,
          mgr_override_can_view_personal_info: false,
          mgr_override_can_cancel_approved_leave: false,
          mgr_override_can_approve_own_leave: false,
          mgr_override_can_book_team_leave: false,
          mgr_override_receive_notifications: true,
          mgr_override_max_approval_days: 0,
          mgr_override_can_create_blackouts: false,
          mgr_override_can_view_reports: false,
          mgr_override_can_view_sickness: false,
          mgr_override_view_all_departments: false,
          mgr_override_manage_all_departments: false,
          manager_view_scope: 'own',
          manager_manage_scope: 'own',
          managed_department_ids: [],
          can_run_payroll: false,
          payroll_scope: 'none',
          payroll_scope_department_ids: [],
          payroll_scope_location_ids: [],
          payroll_scope_user_ids: [],
          reports_to_id: '', approver_id: '', backup_approver_id: '',
          approver_group_id: '', backup_approver_group_id: '', approvers_final: false,
          send_login_email: false,
        });
        setEmergencyContacts([]);
        setSelectedTagIds([]);
        setSchedulePrompt(null);
        setUpcomingChange(null);
      }
    }
  }, [open, employee]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name) {
      toast.error('Please add a first and last name on the Basics step');
      setStep(0);
      return;
    }
    // ⚠️ A payroll permission scoped to NOTHING is a dead setting: the person
    // holds the right and sees an empty report, and nothing on the employees
    // list says why. Rob hit this on 01/09/2026 — the sentence underneath
    // already said "no departments yet", and it saved anyway. Refuse it.
    // (It fails closed rather than open, so this is a usability fix, not a
    // security one — but a permission that silently does nothing is worse than
    // one that won't save.)
    if (form.can_run_payroll
        && ['departments', 'locations', 'people'].includes(form.payroll_scope)
        && !(({ departments: form.payroll_scope_department_ids,
                locations: form.payroll_scope_location_ids,
                people: form.payroll_scope_user_ids }[form.payroll_scope]) || []).length) {
      const what = { departments: 'department', locations: 'location',
                     people: 'person' }[form.payroll_scope];
      toast.error(`Pick at least one ${what} for payroll, or change who payroll covers`);
      const _permIdx = steps.findIndex((s) => s.key === 'permissions');
      if (_permIdx >= 0) setStep(_permIdx);
      return;
    }
    const payload = {
      ...form,
      notice_period_weeks: form.notice_period_weeks ? parseInt(form.notice_period_weeks) : null,
      hours_per_week: form.hours_per_week ? parseFloat(form.hours_per_week) : null,
      annual_allowance: form.annual_allowance ? parseFloat(form.annual_allowance) : null,
      opening_balance_used: form.opening_balance_used ? parseFloat(form.opening_balance_used) : 0,
      yearly_allowance_increment: form.yearly_allowance_increment ? parseFloat(form.yearly_allowance_increment) : 0,
      department_id: form.department_id || null,
      location_id: form.location_id || null,
      public_holiday_region_override: form.public_holiday_region_override || null,
      // Explicit nulls, matching how the ids above are handled. The backend already
      // treats '' as "clear it", but being consistent here means the next person
      // doesn't have to go and check that.
      approver_group_id: form.approver_group_id || null,
      backup_approver_group_id: form.backup_approver_group_id || null,
      emergency_contacts: emergencyContacts.filter(c => c.name),
    };

    // Remove empty strings
    Object.keys(payload).forEach(key => {
      if (payload[key] === '') payload[key] = null;
    });

    // THE SCHEDULE PROMPT (agreed 15 Jul): an edited pattern is ambiguous —
    // a correction ("always been their hours") or a real change from a date.
    // Never guess; ask. Only when editing, only when the pattern actually
    // changed, and only when there IS a new pattern (clearing the override
    // back to the company default is always a correction).
    const _origPattern = (employee && employee.working_hours_override) || null;
    const _newPattern = payload.working_hours_override || null;
    if (isEditing && _newPattern && _newPattern !== _origPattern) {
      setScheduleApplyMode('always');
      setScheduleFromDate('');
      // Capture the employee id NOW: if the dialog were closed mid-prompt the
      // `employee` prop can clear, and the save must still know who it's for.
      setSchedulePrompt({ payload, employeeId: employee.id });
      return;
    }

    await performSave(payload);
  };

  const confirmSchedulePrompt = async () => {
    const { payload } = schedulePrompt;
    if (scheduleApplyMode === 'from_date') {
      if (!scheduleFromDate) {
        toast.error('Pick the date the new hours start from');
        return;
      }
      // A DATED change: their current pattern stays until the date, so the
      // pattern (and its derived weekly hours) must NOT be saved on the user
      // now — it goes to the schedule-change endpoint instead. The backend
      // flips it over on the day (or immediately if dated today).
      const newPattern = payload.working_hours_override;
      const fromDate = scheduleFromDate;
      const targetId = schedulePrompt.employeeId;
      delete payload.working_hours_override;
      delete payload.hours_per_week;
      setSchedulePrompt(null);
      await performSave(payload, async () => {
        const r = await usersApi.setUpcomingSchedule(targetId, {
          pattern: newPattern, effective_from: fromDate,
        });
        if (r.data?.applied_immediately) {
          toast.success('New hours applied from today');
        } else {
          const [_y, _m, _d] = fromDate.split('-');
          toast.success(`Schedule change saved — takes effect ${_d}/${_m}/${_y}`);
        }
      });
    } else {
      // A CORRECTION: saves through the normal path; the backend syncs any
      // dated schedule row currently in force so history agrees.
      setSchedulePrompt(null);
      await performSave(payload);
    }
  };

  const cancelUpcoming = async () => {
    try {
      await usersApi.cancelUpcomingSchedule(employee.id);
      setUpcomingChange(null);
      toast.success('Upcoming schedule change cancelled');
    } catch (error) {
      toast.error('Could not cancel the schedule change');
    }
  };

  const performSave = async (payload, afterUpdate) => {
    setLoading(true);
    let __savedId = null;
    try {
      if (isEditing) {
        // Admin set-password: keep a typed password, drop an empty one.
        if (!payload.password) delete payload.password;
        // Email is normally immutable on edit — but an email-LESS employee
        // (Timetastic-style) can have one ADDED so they can log in. Only strip
        // it from the payload when they already have an email.
        if (employee?.email) delete payload.email;
        delete payload.send_login_email;
        
        // If not admin, remove restricted fields that managers cannot modify
        if (!isAdmin) {
          delete payload.role;
          delete payload.manager_can_view;
          delete payload.manager_can_edit;
          delete payload.is_archived;
          // Payroll access is admin-only (DESIGN-payroll.md). ⚠️ Belt and braces:
          // the backend 403s on these fields for a non-admin regardless. Stripping
          // here just avoids a pointless error toast — it is NOT the enforcement,
          // because a rule that only exists in the front end enforces nothing.
          delete payload.can_run_payroll;
          delete payload.payroll_scope;
          delete payload.payroll_scope_department_ids;
          delete payload.payroll_scope_location_ids;
          delete payload.payroll_scope_user_ids;
        }
        
        await usersApi.update(employee.id, payload);
        if (afterUpdate) await afterUpdate();   // e.g. queue the dated schedule change
        __savedId = employee.id;
        toast.success('Employee updated successfully');
      } else {
        // For new employees, either a password is required OR must_change_password must be set
        if (!payload.password && !payload.must_change_password) {
          toast.error('Password is required for new employees');
          setLoading(false);
          return;
        }
        const __cr = await usersApi.create(payload);
        __savedId = __cr && __cr.data ? __cr.data.id : null;
        toast.success('Employee created successfully');
      }
      
      if (__savedId) { try { await tagsApi.setUserTags(__savedId, selectedTagIds); } catch (e) { /* non-fatal */ } }

      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to save employee';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };
  
  const addEmergencyContact = () => {
    setEmergencyContacts([
      ...emergencyContacts,
      { name: '', relation: '', phone: '', mobile: '', priority: emergencyContacts.length + 1 }
    ]);
  };
  
  const updateEmergencyContact = (index, data) => {
    const newContacts = [...emergencyContacts];
    newContacts[index] = data;
    setEmergencyContacts(newContacts);
  };
  
  const removeEmergencyContact = (index) => {
    setEmergencyContacts(emergencyContacts.filter((_, i) => i !== index));
  };
  
  const [step, setStep] = useState(0);
  // The "land on a tab" effect lives further down, just after `steps` is built — it
  // needs that list to turn a tab key into an index, and duplicating the list here is
  // exactly how two copies drift apart.
  useEffect(() => {
    if (open && employee?.id) {
      dashboardApi.getApprovalSummary(employee.id).then((r) => setApprovalSummary(r.data)).catch(() => setApprovalSummary(null));
    } else {
      setApprovalSummary(null);
      setEntitlementInfo(null);
    }
  }, [open, employee?.id]);

  // Statutory advisory — silent on failure or unmodelled country (D9).
  //
  // ⚠️ IT ANSWERS FOR THE FIGURE BEING TYPED (Rob, 22/10/2026). It used to ask once, for
  // the SAVED allowance, so typing 1 day changed nothing on screen and Save then closed
  // the window — the warning was never seen at the one moment it could help. Now it asks
  // again as the box changes (after a pause, so not on every keystroke), and the server
  // works the total out with the same functions the save uses. Nothing is saved by asking.
  // An empty box means "the company default", exactly as saving it would.
  const _advisoryAsk = useRef(0);
  useEffect(() => {
    if (!open || !employee?.id) return undefined;
    const typed = form.annual_allowance;
    const value = (typed === '' || typed === null || typed === undefined)
      ? companyDefaultAllowance : parseFloat(typed);
    if (!Number.isFinite(value) || value < 0) return undefined;
    const ask = ++_advisoryAsk.current;
    const t = setTimeout(() => {
      entitlementApi.advisoryUser(employee.id, undefined, value)
        // ⚠️ Only the LATEST question's answer may land — a slow reply for "28" must not
        // overwrite the answer for "1" that the admin is looking at.
        .then((r) => { if (ask === _advisoryAsk.current) setEntitlementInfo(r.data?.jurisdiction ? { ...r.data, asked_allowance: value } : null); })
        .catch(() => { if (ask === _advisoryAsk.current) setEntitlementInfo(null); });
    }, 350);
    return () => clearTimeout(t);
  }, [open, employee?.id, form.annual_allowance, companyDefaultAllowance]);

  // Live advisory rest-break check on this employee's pattern OVERRIDE (debounced).
  // No override = they follow the company pattern, whose warnings live in Settings —
  // repeating them here would be noise. [] can mean "compliant" or "country not
  // modelled"; both correctly render as nothing.
  useEffect(() => {
    if (!open || !form.working_hours_override) { setHoursWarnings([]); return; }
    let pattern;
    try { pattern = JSON.parse(form.working_hours_override); } catch (e) { setHoursWarnings([]); return; }
    const t = setTimeout(() => {
      settingsApi.checkWorkingHours({ pattern })
        .then((res) => setHoursWarnings(res.data?.rest_break_warnings || []))
        .catch(() => setHoursWarnings([]));
    }, 500);
    return () => clearTimeout(t);
  }, [open, form.working_hours_override]);

  const isMgrRole = form.role === 'manager' || form.role === 'admin';

  // Departments this person is the NAMED manager of, by name. Comes from the
  // backend as its own field precisely because `managed_department_ids` merges
  // this route with the tick-list, and a screen that has to explain itself
  // cannot use a merged value.
  const namedManagerNames = React.useMemo(() => {
    const ids = employee?.named_manager_department_ids || [];
    return (departments || []).filter((d) => ids.includes(d.id)).map((d) => d.name);
  }, [employee, departments]);
  const stepColors = {
    blue:   { chip: 'bg-blue-500/15 text-blue-600',     tab: 'bg-blue-500/10 text-blue-600 border-blue-500/40',     bar: 'bg-blue-500',     btn: 'bg-blue-600 hover:bg-blue-700' },
    purple: { chip: 'bg-purple-500/15 text-purple-600', tab: 'bg-purple-500/10 text-purple-600 border-purple-500/40', bar: 'bg-purple-500', btn: 'bg-purple-600 hover:bg-purple-700' },
    amber:  { chip: 'bg-amber-500/15 text-amber-600',   tab: 'bg-amber-500/10 text-amber-600 border-amber-500/40',   bar: 'bg-amber-500',   btn: 'bg-amber-600 hover:bg-amber-700' },
    green:  { chip: 'bg-emerald-500/15 text-emerald-600', tab: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/40', bar: 'bg-emerald-500', btn: 'bg-emerald-600 hover:bg-emerald-700' },
    red:    { chip: 'bg-rose-500/15 text-rose-600',     tab: 'bg-rose-500/10 text-rose-600 border-rose-500/40',     bar: 'bg-rose-500',     btn: 'bg-rose-600 hover:bg-rose-700' },
  };
  const steps = [
    { key: 'basics', label: 'Basics', Icon: User, color: 'blue' },
    { key: 'employment', label: 'Employment', Icon: Briefcase, color: 'purple' },
    { key: 'leave', label: 'Leave', Icon: Calendar, color: 'amber' },
    ...(employee?.personal_redacted ? [] : [{ key: 'personal', label: 'Personal', Icon: MapPin, color: 'green' }]),
    ...(isAdmin ? [{ key: 'permissions', label: 'Permissions', Icon: Shield, color: 'red' }] : []),
  ];
  // Open on the requested tab, otherwise start at the beginning. Runs on every open so
  // reopening from the org chart lands on Permissions again rather than wherever the
  // user wandered to last time.
  useEffect(() => {
    if (!open) return;
    const i = initialTab ? steps.findIndex((s) => s.key === initialTab) : -1;
    setStep(i >= 0 ? i : 0);
  // `steps` is rebuilt every render, so listing it would run this on every render.
  // The tab set only changes with the props already listed here.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialTab]);

  const cur = Math.min(step, steps.length - 1);
  const active = steps[cur];
  const ac = stepColors[active.color];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* THE SCHEDULE PROMPT — shown when the working pattern was edited on an
          existing employee. A pattern edit is ambiguous (correction vs a real
          change from a date); we never guess (agreed 15 Jul).
          PORTALLED to document.body: rendered inline it inherits the page's
          stacking contexts and ends up clipped behind the dialog (Rob hit
          exactly that on the first staging test). Gated on `open` so closing
          the dialog also dismisses it. */}
      {open && schedulePrompt && createPortal(
        <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4 pointer-events-auto" data-testid="schedule-prompt">
          <div className="bg-card border border-border rounded-lg shadow-lg max-w-md w-full p-6">
            <h3 className="font-heading text-lg font-semibold mb-1">When do these hours apply from?</h3>
            <p className="text-sm text-muted-foreground mb-4">
              You've changed {form.first_name || 'this employee'}'s working pattern.
            </p>
            <label className="flex items-start gap-3 mb-3 cursor-pointer">
              <input type="radio" className="mt-1 accent-primary"
                     checked={scheduleApplyMode === 'always'}
                     onChange={() => setScheduleApplyMode('always')} />
              <span>
                <span className="block text-sm font-medium">These have always been their hours</span>
                <span className="block text-xs text-muted-foreground">
                  Fixing a mistake — the record was wrong. No schedule change is logged.
                </span>
              </span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="radio" className="mt-1 accent-primary"
                     checked={scheduleApplyMode === 'from_date'}
                     onChange={() => setScheduleApplyMode('from_date')} />
              <span className="flex-1">
                <span className="block text-sm font-medium">They change from a date</span>
                <span className="block text-xs text-muted-foreground mb-2">
                  A real change — current hours stay in place until this date, and
                  leave booked before it stays priced at the old hours.
                </span>
                {scheduleApplyMode === 'from_date' && (
                  <Input type="date" value={scheduleFromDate}
                         min={new Date().toISOString().slice(0, 10)}
                         onChange={(e) => setScheduleFromDate(e.target.value)}
                         className="input-hive" />
                )}
              </span>
            </label>
            <div className="flex justify-end gap-2 mt-6">
              <Button type="button" variant="outline" onClick={() => setSchedulePrompt(null)} disabled={loading}>
                Back
              </Button>
              <Button type="button" onClick={confirmSchedulePrompt} disabled={loading}>
                Save
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}
      <DialogContent
        className="bg-card border-border max-w-5xl max-h-[90vh] overflow-y-auto p-0"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader className="px-6 pt-6 pb-0">
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${ac.chip}`}>
              <active.Icon className="w-5 h-5" />
            </div>
            <DialogTitle className="font-heading text-xl">
              {viewOnly ? 'View employee details' : (isEditing ? 'Edit employee' : 'Add new employee')}
            </DialogTitle>
          </div>
          {viewOnly && (
            <p className="text-sm text-muted-foreground mt-2">You are viewing this employee's details in read-only mode.</p>
          )}
        </DialogHeader>

        <div className="h-1 bg-secondary mt-4">
          <div className={`h-full transition-all ${ac.bar}`} style={{ width: `${((cur + 1) / steps.length) * 100}%` }} />
        </div>

        <div className="flex flex-wrap gap-2 px-6 py-3 border-b border-border">
          {steps.map((stp, i) => {
            const c = stepColors[stp.color];
            const on = i === cur;
            return (
              <button
                key={stp.key}
                type="button"
                data-nav="true"
                onClick={() => setStep(i)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm border transition-colors ${on ? c.tab + ' font-medium' : 'border-border text-muted-foreground hover:text-foreground'}`}
              >
                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[11px] ${on ? c.bar + ' text-white' : 'bg-secondary'}`}>{i + 1}</span>
                <stp.Icon className="w-4 h-4" />
                {stp.label}
              </button>
            );
          })}
        </div>

        <form onSubmit={handleSubmit} className="px-6 pb-6 pt-4">
          <style>{viewOnly ? `.vo input,.vo select,.vo textarea,.vo button:not([data-nav]){pointer-events:none;opacity:.8;}` : ''}</style>
          <div className={viewOnly ? 'vo' : ''}>
            <div className="min-h-[260px] space-y-4">

              {active.key === 'basics' && (
                <>
                  <p className="text-sm text-muted-foreground">The essentials to create the account. You can save after this step — everything else can be filled in later.</p>
                  <div className="flex flex-col sm:flex-row gap-6">
                    <ProfilePictureCropper currentImage={form.profile_picture_url} onImageChange={(img) => setForm({ ...form, profile_picture_url: img })} />
                    <div className="flex-1 space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div><Label>First name *</Label><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="input-hive mt-1" required data-testid="emp-firstname" /></div>
                        <div><Label>Last name *</Label><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="input-hive mt-1" required data-testid="emp-lastname" /></div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Email</Label>
                          <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-hive mt-1" placeholder="Optional" disabled={isEditing && !!employee?.email} data-testid="emp-email" />
                          {!(isEditing && !!employee?.email) && !form.email && (
                            <p className="text-xs text-muted-foreground mt-1">
                              It's OK to leave this blank. Their leave and calendar are still tracked — they just won't be able to log in, be a department manager, or approve leave for others.
                            </p>
                          )}
                        </div>
                        {!employee?.personal_redacted && (
                          <div><Label>Date of birth</Label><Input type="date" value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} className="input-hive mt-1" /></div>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Role</Label>
                          <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                            <SelectTrigger className="input-hive mt-1"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="employee">Employee</SelectItem>
                              <SelectItem value="manager">Manager</SelectItem>
                              <SelectItem value="admin">Admin</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Department</Label>
                          <Select value={form.department_id || 'none'} onValueChange={(v) => setForm({ ...form, department_id: v === 'none' ? '' : v })}>
                            <SelectTrigger className="input-hive mt-1"><SelectValue placeholder="Select..." /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">No department</SelectItem>
                              {departments.map((d) => (<SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      {availableTags.length > 0 && (
                        <div>
                          <Label>Tags</Label>
                          <div className="flex flex-wrap gap-2 mt-1">
                            {availableTags.map((tg) => {
                              const on = selectedTagIds.includes(tg.id);
                              return (
                                <button
                                  key={tg.id}
                                  type="button"
                                  onClick={() => setSelectedTagIds(on ? selectedTagIds.filter((x) => x !== tg.id) : [...selectedTagIds, tg.id])}
                                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${on ? '' : 'text-muted-foreground border-border hover:text-foreground'}`}
                                  style={on ? { backgroundColor: (tg.color || '#64748b') + '22', color: tg.color || '#64748b', borderColor: (tg.color || '#64748b') + '66' } : {}}
                                >
                                  {tg.name}
                                </button>
                              );
                            })}
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">Click to tag this person. Manage tags under Employees → Tags.</p>
                        </div>
                      )}
                    </div>
                  </div>
                  {isEditing && isAdmin && (
                    <div>
                      <Label>Set a new password (optional)</Label>
                      <div className="relative">
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          value={form.password}
                          onChange={(e) => setForm({ ...form, password: e.target.value })}
                          className="input-hive mt-1 pr-10"
                          minLength={MIN_PASSWORD_LENGTH}
                          placeholder="Leave blank to keep their current password"
                          data-testid="emp-set-password"
                        />
                        <button type="button" data-nav="true" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">{showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {PASSWORD_HINT}. They'll be asked to change it the first
                        time they log in. Tip: "Copy invite link" on the Employees
                        list lets them set their own instead.
                      </p>
                    </div>
                  )}
                  {!isEditing && (
                    <div className="space-y-3">
                      <div>
                        <Label>Password *</Label>
                        <div className="relative">
                          <Input type={showPassword ? 'text' : 'password'} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="input-hive mt-1 pr-10" required={!isEditing && !form.must_change_password} minLength={MIN_PASSWORD_LENGTH} placeholder={form.must_change_password ? 'Will be set by user' : PASSWORD_HINT} disabled={form.must_change_password} data-testid="emp-password" />
                          <button type="button" data-nav="true" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" disabled={form.must_change_password}>{showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
                        </div>
                        {!form.must_change_password && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {PASSWORD_HINT}. They'll be asked to choose their own the first time
                            they log in, so this one only needs to get them through the door.
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 p-3 bg-secondary/50 rounded-lg">
                        <Checkbox id="must_change_password" checked={form.must_change_password} onCheckedChange={(checked) => setForm({ ...form, must_change_password: checked, password: checked ? '' : form.password })} />
                        <div>
                          <Label htmlFor="must_change_password" className="text-sm font-medium cursor-pointer">User sets password on first login</Label>
                          <p className="text-xs text-muted-foreground">Send a password setup email instead of setting a password now</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 p-3 bg-secondary/30 rounded-lg">
                        <input type="checkbox" checked={form.send_login_email && !!form.email} onChange={(e) => setForm({ ...form, send_login_email: e.target.checked })} className="w-4 h-4 rounded accent-primary disabled:opacity-40" id="send-login-email" disabled={!form.email} />
                        <label htmlFor="send-login-email" className={`text-sm ${form.email ? 'cursor-pointer' : 'text-muted-foreground'}`}>Email login details to the employee{!form.email && ' (needs an email address)'}</label>
                      </div>
                    </div>
                  )}
                </>
              )}

              {active.key === 'employment' && (
                <>
                  <p className="text-sm text-muted-foreground">Job details and working pattern.</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Job title</Label><Input value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} className="input-hive mt-1" placeholder="e.g. Maths Teacher" /></div>
                    <div><Label>Start date</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="input-hive mt-1" /></div>
                  </div>
                  {locationsEnabled && locations.length > 0 && (
                    <div>
                      <Label>Location</Label>
                      <Select value={form.location_id || 'none'} onValueChange={(v) => setForm({ ...form, location_id: v === 'none' ? '' : v })} disabled={viewOnly}>
                        <SelectTrigger className="input-hive mt-1"><SelectValue placeholder="Select location..." /></SelectTrigger>
                        <SelectContent><SelectItem value="none">No location</SelectItem>{locations.map((b) => (<SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>))}</SelectContent>
                      </Select>
                    </div>
                  )}
                  {/* ⚠️ WAS THREE HARD-CODED UK REGIONS, SHOWN TO EVERYONE. That was the
                      fifth hand-typed copy of this list in the codebase, and the one
                      most likely to do damage: offering "England & Wales / Scotland /
                      Northern Ireland" to someone at the Algiers office is nonsense, and
                      choosing one would hand them UK bank holidays. Shown only where the
                      person's country actually has regions. Country unknown falls back to
                      the UK, so nothing changes for the customers who have one already. */}
                  {overrideRegions.length > 0 && (
                  <div>
                    <Label>Public holiday region (override)</Label>
                    <Select value={form.public_holiday_region_override || 'default'} onValueChange={(v) => setForm({ ...form, public_holiday_region_override: v === 'default' ? '' : v })} disabled={viewOnly}>
                      <SelectTrigger className="input-hive mt-1"><SelectValue placeholder="Use location or company default" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="default">Use location or company default</SelectItem>
                        {overrideRegions.map((r) => (
                          <SelectItem key={r.code} value={r.code}>{r.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground mt-1">Only set this if this person follows a different region's bank holidays to their office.</p>
                  </div>
                  )}
                  <div className="grid grid-cols-3 gap-4">
                    <div><Label>Employee ID</Label><Input value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} className="input-hive mt-1" placeholder="EMP001" /></div>
                    <div><Label>Payroll ID</Label><Input value={form.payroll_id} onChange={(e) => setForm({ ...form, payroll_id: e.target.value })} className="input-hive mt-1" /></div>
                    <div><Label>Notice period (weeks)</Label><Input type="number" value={form.notice_period_weeks} onChange={(e) => setForm({ ...form, notice_period_weeks: e.target.value })} className="input-hive mt-1" min="0" /></div>
                  </div>
                  <div className="border-t border-border pt-4 space-y-3">
                    <div className="w-48"><Label>Hours per week</Label><Input type="number" step="0.5" value={form.hours_per_week} onChange={(e) => setForm({ ...form, hours_per_week: e.target.value })} className="input-hive mt-1" placeholder="37.5" /></div>
                    {/* ⚠️ INHERIT UNLESS OVERRIDDEN — the same shape as the Location
                        editor, and it is here because of Rob's test on 07/08/2026.
                        He set an employee to "Mon-Fri" on this card and the wallchart
                        ignored it. The app was RIGHT: the person had no saved pattern at
                        all. This editor was pre-filling the COMPANY default as ticked
                        boxes, which is indistinguishable from a pattern somebody chose.
                        The caption even said "leave blank to use company default" while
                        showing something that wasn't blank — and by then it was their
                        LOCATION they were following, not the company. */}
                    <div>
                      <Label className="mb-2 block">Working week</Label>
                      <div className="space-y-2">
                        <label className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer ${!form.working_hours_override ? 'border-primary ring-1 ring-primary' : 'border-border'}`}>
                          <input
                            type="radio"
                            className="mt-1"
                            checked={!form.working_hours_override}
                            onChange={() => {
                              // ⚠️ CLEARING BOTH FIELDS IS THE POINT. `working_hours_override`
                              // NULL is what "follow my location" means to the server, and
                              // a stale hours_per_week left behind would be a second
                              // opinion about the same week.
                              setForm({ ...form, working_hours_override: '', hours_per_week: '' });
                            }}
                            data-testid="hours-follow-location"
                          />
                          <span>
                            <span className="block text-sm font-medium">
                              Same as {inheritedFrom(chosenLocation, companyHours)}
                            </span>
                            <span className="block text-xs text-muted-foreground">
                              {inheritedDays.length
                                ? `${describeWorkingDays(inheritedDays)} — changes here if their office does`
                                : 'Follows their office, or the company if it has no week of its own'}
                            </span>
                          </span>
                        </label>
                        <label className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer ${form.working_hours_override ? 'border-primary ring-1 ring-primary' : 'border-border'}`}>
                          <input
                            type="radio"
                            className="mt-1"
                            checked={!!form.working_hours_override}
                            onChange={() => {
                              // ⚠️ START FROM WHAT THEY ALREADY FOLLOW, not from Monday to
                              // Friday. An admin at a Sunday-to-Thursday office should not
                              // have to rebuild their own office's week before changing one
                              // day of it. Mirrors `weekWhenOverridden` on the Locations page.
                              const start = inheritedWeek(chosenLocation, companyHours);
                              const wk = computeWeeklyHours(start, companyHours);
                              setForm({
                                ...form,
                                working_hours_override: start ? JSON.stringify(start) : '',
                                hours_per_week: wk ? String(Math.round(wk * 100) / 100) : '',
                              });
                            }}
                            data-testid="hours-own-pattern"
                          />
                          <span>
                            <span className="block text-sm font-medium">Different for this person</span>
                            <span className="block text-xs text-muted-foreground">
                              Set their own days and hours — this is what a part-timer needs
                            </span>
                          </span>
                        </label>
                      </div>
                      {form.working_hours_override && (
                        <div className="mt-3">
                          <WorkingHoursEditor hours={JSON.parse(form.working_hours_override)} onChange={(h) => { const wk = computeWeeklyHours(h, companyHours); setForm({ ...form, working_hours_override: h, hours_per_week: wk ? String(Math.round(wk * 100) / 100) : '' }); }} companyHours={companyHours} warnings={hoursWarnings} />
                        </div>
                      )}
                    </div>
                    {isEditing && upcomingChange && (
                      <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 flex items-center justify-between gap-3" data-testid="upcoming-schedule-change">
                        <div>
                          <p className="text-sm font-medium">Upcoming schedule change</p>
                          <p className="text-xs text-muted-foreground">
                            From {new Date(upcomingChange.effective_from + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                            {' — '}{Math.round((upcomingChange.weekly_minutes / 60) * 100) / 100} hrs/week.
                            Their current hours stay in place until then.
                          </p>
                        </div>
                        <Button type="button" variant="outline" size="sm" onClick={cancelUpcoming}>
                          Cancel change
                        </Button>
                      </div>
                    )}
                  </div>
                </>
              )}

              {active.key === 'leave' && (
                <>
                  <p className="text-sm text-muted-foreground">Allowance and holiday rules.</p>
                  {!employee?.balances_redacted && (<div><Label>Annual allowance (days)</Label><Input type="number" step="0.5" value={form.annual_allowance} onChange={(e) => setForm({ ...form, annual_allowance: e.target.value })} className="input-hive mt-1" placeholder={`Company default (${companyDefaultAllowance})`} />
                    {/* ⚠️ `theirDayMinutes` IS THEIR OWN DAY LENGTH, so "4 days" can also
                        be shown as the hours THEY read. A day of leave is worth this
                        person's day, not the company's — the same reason the allowance row
                        stamps a `day_minutes_basis`.

                        ⚠️ ROB'S FIND, 13/08/2026 — a Monday-only employee holding the full
                        company default of 20 days, for a job of about 52 days a year, with
                        nothing anywhere saying a word. The moment the pattern is set is the
                        moment somebody can still think about it. It SUGGESTS and never
                        applies, and it offers their own number as well as ours. */}
                    <AllowanceProRataNote
                      name={form.first_name || null}
                      allowanceDays={form.annual_allowance === ''
                        ? companyDefaultAllowance : parseFloat(form.annual_allowance)}
                      companyDefaultDays={companyDefaultAllowance}
                      theirDaysAWeek={theirDaysAWeek}
                      companyDaysAWeek={companyDaysAWeek}
                      dayMinutes={companyDayMinutes}
                      theirDayMinutes={theirWeeklyMinutes && theirDaysAWeek
                        ? theirWeeklyMinutes / theirDaysAWeek : null}
                      displayUnit={form.display_unit || 'days'}
                      bankHolidaysLanding={bankHolidaysLanding}
                      onChoose={(days) => {
                        if (days === null) return;   // "leave it" writes nothing
                        setForm({ ...form, annual_allowance: String(days) });
                      }}
                    />
                  </div>)}
                  {/* Statutory advisory (D4: never blocking; D9: nothing for unmodelled
                      countries). The floor is computed from THIS person's schedule —
                      the whole point of the entitlement engine. */}
                  {(() => {
                    // Q25 a (Rob, 22/10/2026): three short lines instead of one sentence
                    // holding two different "19"s. Wording lives in lib/statutoryBox.js.
                    const box = statutoryBox({ info: entitlementInfo, firstName: form.first_name, startDate: form.start_date });
                    if (!box) return null;
                    const source = entitlementInfo.source_url && (
                      <a href={entitlementInfo.source_url} target="_blank" rel="noreferrer" className="text-primary hover:underline">source</a>
                    );
                    if (!box.below) {
                      return (
                        <p className="text-xs text-muted-foreground" data-testid="statutory-meets">
                          ✓ {box.title}: {box.gets ? <>{box.who} gets {box.gets.days}; </> : null}
                          the minimum{box.intro ? ' this year' : ''} is {box.minimum.days}
                          {box.minimum.detail ? ` (${box.minimum.detail})` : ''}.{source && <> {source}</>}
                        </p>
                      );
                    }
                    return (
                      <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30" data-testid="statutory-below">
                        <p className="text-sm font-medium text-amber-500">{box.title}</p>
                        {box.intro && <p className="text-xs text-muted-foreground mt-1">{box.intro}</p>}
                        <ul className="text-xs text-muted-foreground mt-1 space-y-0.5 list-disc pl-4">
                          <li>{box.minimum.label}: <strong>{box.minimum.days}</strong>{box.minimum.detail ? ` (${box.minimum.detail})` : ''}</li>
                          {box.gets && (
                            <li>{box.gets.label}: <strong>{box.gets.days}</strong>{box.gets.detail ? ` — ${box.gets.detail}` : ''}</li>
                          )}
                        </ul>
                        <p className="text-xs text-muted-foreground mt-1">
                          {box.short && <strong className="text-amber-500">{box.short}</strong>}
                          {' '}Nothing is blocked; a contract may give more.{source && <> {source}</>}
                        </p>
                      </div>
                    );
                  })()}
                  {!isEditing && (
                    <div>
                      <Label>Holiday already taken this leave year (days)</Label>
                      <Input type="number" step="0.5" min="0" value={form.opening_balance_used} onChange={(e) => setForm({ ...form, opening_balance_used: e.target.value })} className="input-hive mt-1" placeholder="0" disabled={viewOnly} />
                      <p className="text-xs text-muted-foreground mt-1">Migrating mid-year? Enter days already taken so their balance is correct. New starters are auto pro-rated from their start date.</p>
                    </div>
                  )}
                  {!employee?.balances_redacted && (<div className="w-64"><Label>Yearly increment (extra days per year worked)</Label><Input type="number" step="0.5" value={form.yearly_allowance_increment} onChange={(e) => setForm({ ...form, yearly_allowance_increment: e.target.value })} className="input-hive mt-1" placeholder="0" /></div>)}

                  {/* HOW THEIR LEAVE IS COUNTED — A3, Rob 22/10/2026 (Q21 c, Q22 b).
                      This used to be display only ("Show their leave in"). It now decides
                      BOTH what they see AND what a booking is judged on: in days a day off
                      costs 1 day and half a day 0.5, whatever their lengths; in hours a
                      booking costs its real hours. See backend/services/leave_units.py.
                      Bookings already made keep what they cost — nothing is recalculated,
                      so switching is still safe both ways. */}
                  {!employee?.balances_redacted && (
                    <div>
                      <div className="w-64">
                        <Label>Count their leave in</Label>
                        <Select value={form.display_unit || 'days'} onValueChange={(v) => setForm({ ...form, display_unit: v })}>
                          <SelectTrigger className="input-hive mt-1"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="days">Days</SelectItem>
                            <SelectItem value="hours">Hours</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {(() => {
                          const mins = employee?.allowance_remaining_minutes;
                          const days = employee?.allowance_remaining;
                          const hasBalance = isEditing && days !== null && days !== undefined;
                          const unit = form.display_unit || 'days';
                          if (hasBalance && unit === 'hours' && (mins === null || mins === undefined)) {
                            // Un-backfilled row: be honest rather than showing "0h".
                            return 'Their balance has no hours stored yet, so it will keep showing in days until their next save or rollover.';
                          }
                          const what = 'Decides how their balance is shown and what each booking takes off it. Bookings already made keep what they cost.';
                          if (hasBalance) {
                            return `Their balance shows as ${formatLeave(days, { unit, minutes: mins })}. ${what}`;
                          }
                          return what;
                        })()}
                      </p>
                      {/* ⚠️ THE QUESTION, NOT AN ANSWER (Q22 b). Shown only for somebody counted
                          in days whose week varies, until the admin answers for THIS week —
                          the server decides (`leave_unit_review`), from the same rule as the
                          Perfection Box. Nothing switches unless a button is pressed. */}
                      {isEditing && isAdmin && employee?.leave_unit_review && !unitAnswered && (
                        <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-500/10 p-3 text-sm" data-testid="leave-unit-flag">
                          <p className="font-medium text-amber-700 dark:text-amber-400">
                            {form.first_name || 'Their'}{form.first_name ? "'s" : ''} working days aren&rsquo;t all the same length
                            {employee.uneven_week ? ` (${employee.uneven_week})` : ''}.
                          </p>
                          <p className="text-muted-foreground mt-1">
                            In days, every day off costs 1 day whatever its length. In hours, each costs its own hours.
                            Use hours unless {form.first_name || 'their'}{form.first_name ? "'s" : ''} contract gives a number of days.
                          </p>
                          <div className="flex flex-wrap gap-2 mt-2">
                            <Button type="button" size="sm" disabled={viewOnly}
                              data-testid="leave-unit-hours"
                              onClick={async () => {
                                try {
                                  const r = await usersApi.setLeaveUnit(employee.id, 'hours');
                                  setForm((f) => ({ ...f, display_unit: 'hours' }));
                                  setUnitAnswered(true);
                                  toast.success(r.data?.message || 'Now counted in hours');
                                } catch (e) {
                                  toast.error(e.response?.data?.detail || 'That didn’t save');
                                }
                              }}>
                              Count in hours
                            </Button>
                            <Button type="button" size="sm" variant="outline" disabled={viewOnly}
                              data-testid="leave-unit-days"
                              onClick={async () => {
                                try {
                                  const r = await usersApi.setLeaveUnit(employee.id, 'days');
                                  setForm((f) => ({ ...f, display_unit: 'days' }));
                                  setUnitAnswered(true);
                                  toast.success(r.data?.message || 'Stays on days');
                                } catch (e) {
                                  toast.error(e.response?.data?.detail || 'That didn’t save');
                                }
                              }}>
                              Keep days — it&rsquo;s in the contract
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  <div className="flex items-center gap-3 p-4 bg-secondary/30 rounded-lg">
                    <input type="checkbox" id="works_public_holidays" checked={form.works_public_holidays} onChange={(e) => setForm({ ...form, works_public_holidays: e.target.checked })} className="w-4 h-4 rounded border-input accent-primary" />
                    <div>
                      <label htmlFor="works_public_holidays" className="font-medium cursor-pointer">Works on public holidays</label>
                      <p className="text-xs text-muted-foreground mt-0.5">Tick if this employee is contracted to work on bank holidays (e.g. retail/care staff)</p>
                    </div>
                  </div>
                </>
              )}

              {active.key === 'personal' && (
                <>
                  <p className="text-sm text-muted-foreground">The employee&rsquo;s own address & contact details, and who to contact in an emergency &mdash; all optional.</p>

                  {/* Employee's own address & contact */}
                  <div className="rounded-lg border border-border p-4 space-y-3">
                    <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-emerald-500" /><h4 className="font-medium">Employee&rsquo;s address &amp; contact</h4></div>
                    <AddressLookup initialPostcode={form.postcode} disabled={viewOnly} onPostcodeChange={(v) => setForm((f) => ({ ...f, postcode: v }))} onAddressSelect={(address) => { setForm({ ...form, address_line_1: address.address_line_1, address_line_2: address.address_line_2, town: address.city, county: address.county, postcode: address.postcode, country: address.country || 'United Kingdom' }); }} />
                    <div><Label>Address line 1</Label><Input value={form.address_line_1} onChange={(e) => setForm({ ...form, address_line_1: e.target.value })} className="input-hive mt-1" placeholder="Street address" /></div>
                    <div><Label>Address line 2</Label><Input value={form.address_line_2} onChange={(e) => setForm({ ...form, address_line_2: e.target.value })} className="input-hive mt-1" /></div>
                    <div className="grid grid-cols-2 gap-4">
                      <div><Label>Town</Label><Input value={form.town} onChange={(e) => setForm({ ...form, town: e.target.value })} className="input-hive mt-1" /></div>
                      <div>
                        <Label>County</Label>
                        <Select value={form.county || ''} onValueChange={(v) => setForm({ ...form, county: v })}>
                          <SelectTrigger className="input-hive mt-1"><SelectValue placeholder="Select county..." /></SelectTrigger>
                          <SelectContent className="max-h-[300px]">{UK_COUNTIES.map((county) => (<SelectItem key={county} value={county}>{county}</SelectItem>))}</SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4 pt-1">
                      <div><Label>Home phone</Label><Input value={form.home_phone} onChange={(e) => setForm({ ...form, home_phone: e.target.value })} className="input-hive mt-1" /></div>
                      <div><Label>Mobile phone</Label><Input value={form.mobile_phone} onChange={(e) => setForm({ ...form, mobile_phone: e.target.value })} className="input-hive mt-1" /></div>
                      <div><Label>Work phone</Label><Input value={form.work_phone} onChange={(e) => setForm({ ...form, work_phone: e.target.value })} className="input-hive mt-1" /></div>
                    </div>
                  </div>

                  {/* Emergency contacts &mdash; a separate person to call if something happens */}
                  <div className="rounded-lg border border-border p-4 space-y-4">
                    <div>
                      <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-red-500" /><h4 className="font-medium">Emergency contacts</h4></div>
                      <p className="text-xs text-muted-foreground mt-1">Someone to reach if there&rsquo;s an emergency (next of kin, partner, etc.). Click &ldquo;Add emergency contact&rdquo; to enter their name and number.</p>
                    </div>
                    {emergencyContacts.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-2">No emergency contacts added yet</p>
                    ) : (
                      emergencyContacts.map((contact, idx) => (
                        <EmergencyContactItem key={idx} contact={contact} index={idx} onUpdate={(data) => updateEmergencyContact(idx, data)} onRemove={() => removeEmergencyContact(idx)} employeeAddress={{ address_line_1: form.address_line_1, address_line_2: form.address_line_2, address_line_3: form.address_line_3, town: form.town, county: form.county, postcode: form.postcode }} />
                      ))
                    )}
                    <Button type="button" variant="outline" className="w-full" onClick={addEmergencyContact}><Plus className="w-4 h-4 mr-2" />Add emergency contact</Button>
                  </div>
                </>
              )}

              {active.key === 'permissions' && (
                <div className="space-y-5">
                  {/* Ringed when you arrive from the org chart's needs-attention strip,
                      so the thing that's missing is the thing your eye lands on. Plain
                      border otherwise — a permanent highlight is just decoration. */}
                  <div className={`rounded-lg border p-4 space-y-4 ${
                    highlightApproval
                      ? 'border-amber-500/60 bg-amber-500/5 ring-1 ring-amber-500/30'
                      : 'border-border'}`}>
                    <h4 className="font-medium flex items-center gap-2"><Shield className="w-4 h-4 text-rose-500" /> Approval & reporting</h4>
                    {highlightApproval && (
                      <p className="text-sm text-amber-700">
                        Nobody is set to approve this person's leave, so it falls through
                        to an admin. Set a manager or an approver below.
                      </p>
                    )}
                    <p className="text-sm text-muted-foreground">Who is this person's manager, and who signs off their leave? Approvers set here take top priority over any company or department rules.</p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="flex items-center gap-1.5">Reports to (manager) <InfoTip text="Their line manager. Used for the Org Chart, and as their leave approver if ‘Approved by’ is left as ‘Same as manager’." /></Label>
                        <Select value={form.reports_to_id || 'none'} onValueChange={(v) => setForm({ ...form, reports_to_id: v === 'none' ? '' : v })} disabled={viewOnly}>
                          <SelectTrigger className="input-hive mt-1"><SelectValue placeholder="Select manager..." /></SelectTrigger>
                          <SelectContent className="max-h-[260px]"><SelectItem value="none">No manager</SelectItem>{managerOptions(form.reports_to_id).map((p) => (<SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>))}</SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="flex items-center gap-1.5">Approved by <InfoTip text="Who signs off their leave. Takes priority over their manager. Leave as ‘Same as manager’ to route to their line manager instead. Only managers and admins can sign off leave." /></Label>
                        <Select
                          value={slotValue(form.approver_id, form.approver_group_id) || 'manager'}
                          onValueChange={(v) => (v === 'manager'
                            ? setForm({ ...form, approver_id: '', approver_group_id: '' })
                            : applySlot(v, 'approver_id', 'approver_group_id'))}
                          disabled={viewOnly}
                        >
                          <SelectTrigger className="input-hive mt-1"><SelectValue placeholder="Same as manager" /></SelectTrigger>
                          <SelectContent className="max-h-[260px]">
                            <SelectItem value="manager" disabled={sameAsManagerBlocked}>Same as manager</SelectItem>
                            {approvalGroups.map((g) => (
                              <SelectItem key={g.id} value={`${GROUP_PREFIX}${g.id}`}>
                                {g.name} (any of {g.usable_members})
                              </SelectItem>
                            ))}
                            {approverOptions(form.approver_id).map((p) => (<SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>))}
                          </SelectContent>
                        </Select>
                        {sameAsManagerBlocked && (
                          <p className="text-xs text-amber-600 dark:text-amber-500 mt-1.5">
                            {reportsToPerson?.first_name} {reportsToPerson?.last_name} isn’t a manager, so they can’t sign off leave.
                            Choose someone else here, or make them a manager — you can limit what a manager is allowed to do
                            in Settings → Manager permissions.
                          </p>
                        )}
                      </div>
                      <div>
                        <Label className="flex items-center gap-1.5">Escalates to <InfoTip text="Who picks it up if the approver above can’t act — because they’re the one requesting, or they’re a manager who’s over their approval limit. Not a stand-in for when someone’s on holiday." /></Label>
                        <Select
                          value={slotValue(form.backup_approver_id, form.backup_approver_group_id) || 'none'}
                          onValueChange={(v) => (v === 'none'
                            ? setForm({ ...form, backup_approver_id: '', backup_approver_group_id: '' })
                            : applySlot(v, 'backup_approver_id', 'backup_approver_group_id'))}
                          disabled={viewOnly}
                        >
                          <SelectTrigger className="input-hive mt-1"><SelectValue placeholder="None" /></SelectTrigger>
                          <SelectContent className="max-h-[260px]">
                            <SelectItem value="none">None</SelectItem>
                            {approvalGroups.map((g) => (
                              <SelectItem key={g.id} value={`${GROUP_PREFIX}${g.id}`}>
                                {g.name} (any of {g.usable_members})
                              </SelectItem>
                            ))}
                            {approverOptions(form.backup_approver_id).map((p) => (<SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg">
                      <div><Label className="font-medium flex items-center gap-1.5">These approvers are final <InfoTip text="On: only the approver(s) above sign off. Off: the request can still escalate to company or department approval policies on top." /></Label><p className="text-xs text-muted-foreground">When on, only the approver(s) above sign off — higher company/department tiers are skipped.</p></div>
                      <Switch checked={!!form.approvers_final} onCheckedChange={(c) => setForm({ ...form, approvers_final: c })} disabled={viewOnly} />
                    </div>
                    {/* Lasting record of a handover. When this person was archived an
                        admin passed their approval duties on, and restoring them
                        deliberately doesn't move those back — so without this the fact
                        lives only in a toast the admin may never have read. */}
                    {employee?.handover_to_name && (
                      <div className="rounded-lg border border-border bg-secondary/40 p-3 text-xs text-muted-foreground">
                        Their approval duties were passed to <strong className="text-foreground">{employee.handover_to_name}</strong> when
                        they left. Those weren't moved back when they returned — update the fields above if they're picking them up again.
                      </div>
                    )}

                    {approvalSummary && (approvalSummary.policy || (approvalSummary.approvers || []).length > 0) && (
                      <div className="rounded-lg border border-rose-500/30 bg-rose-500/5 p-3 space-y-1.5">
                        <div className="flex items-center gap-2 text-sm font-medium text-rose-600">
                          <Shield className="w-4 h-4 flex-shrink-0" />
                          {approvalSummary.policy ? (
                            <span>Approval policy: {approvalSummary.policy.name}{approvalSummary.policy.scope === 'USER' ? ' (individual)' : approvalSummary.policy.scope === 'DEPARTMENT' ? ' (department)' : ' (company default)'}</span>
                          ) : (
                            <span>Current approval chain</span>
                          )}
                        </div>
                        {approvalSummary.policy && (
                          <p className="text-xs text-muted-foreground">
                            Needs {approvalSummary.policy.levels} approval{approvalSummary.policy.levels === 1 ? '' : 's'} in order{approvalSummary.policy.scope !== 'COMPANY' ? ' — managed under Settings \u2192 Approval Flow.' : '.'}
                          </p>
                        )}
                        {(approvalSummary.approvers || []).length > 0 && (
                          <ol className="space-y-0.5 pt-0.5">
                            {approvalSummary.approvers.map((a, i) => (
                              <li key={i} className="flex items-center gap-2 text-sm">
                                <span className="w-4 h-4 rounded-full bg-rose-500/15 text-rose-600 text-[10px] font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
                                <span className="text-foreground">{a.name}</span>
                                {a.note && <span className="text-xs text-muted-foreground">&middot; {a.note}</span>}
                              </li>
                            ))}
                          </ol>
                        )}
                        <p className="text-[11px] text-muted-foreground">Reflects the saved configuration for this employee.</p>
                      </div>
                    )}
                    {employee?.id && !viewOnly && (
                      <div className="px-1">
                        {approvalSummary?.policy?.scope === 'USER' ? (
                          <button type="button" onClick={() => window.open(`/settings/approval-policies?edit=${approvalSummary.policy.id}`, '_blank')} className="text-sm text-rose-600 hover:underline font-medium">
                            Edit this person&rsquo;s approval flow &rarr;
                          </button>
                        ) : (
                          <button type="button" onClick={() => window.open(`/settings/approval-policies?new_user=${employee.id}`, '_blank')} className="text-sm text-rose-600 hover:underline font-medium">
                            Set up a custom multi-step flow for this person &rarr;
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {isMgrRole && (
                    <div className="rounded-lg border border-border p-4 space-y-3">
                      <h4 className="font-medium flex items-center gap-2"><MapPin className="w-4 h-4 text-emerald-500" /> Reach</h4>
                      <p className="text-sm text-muted-foreground">How far this manager can see and act. &ldquo;Manage&rdquo; can&rsquo;t exceed &ldquo;View&rdquo;.</p>
                      {!form.department_id && (form.manager_view_scope === 'own' || form.manager_manage_scope === 'own') && (
                        <div className="text-xs rounded-lg border border-amber-500/40 bg-amber-500/10 p-2.5 text-amber-700">
                          This person isn&rsquo;t assigned to a department, so an &ldquo;Own department&rdquo; reach means they&rsquo;ll only see themselves. Assign them a department, or use Specific / All.
                        </div>
                      )}
                      {/*
                        ⚠️ THERE ARE TWO WAYS TO REACH A DEPARTMENT, AND THE
                        TICK-LIST IS ONLY ONE OF THEM.

                        A person reaches a department if it is ticked below, OR
                        if they are its NAMED MANAGER (set on the Departments
                        screen). Unticking cannot clear the second one.

                        Rob hit this on 10/09/2026: he unticked Diversity for
                        someone he thought of as "the manager of Diversity", the
                        save was refused, and nothing on screen explained that
                        the tick was that person's ONLY route — the named
                        manager was somebody else entirely. The rule was right
                        and the screen was silent about it.
                      */}
                      {namedManagerNames.length > 0 && (
                        <div className="text-xs rounded-lg border border-border bg-secondary/40 p-2.5 text-muted-foreground">
                          Also reaches <span className="font-medium text-foreground">{namedManagerNames.join(', ')}</span> as
                          their named manager — set on the Departments screen, and not changed by the ticks below.
                        </div>
                      )}
                      {(form.manager_view_scope === 'specific' || form.manager_manage_scope === 'specific')
                        && !(form.managed_department_ids || []).length
                        && namedManagerNames.length === 0 && (
                        <div className="text-xs rounded-lg border border-amber-500/40 bg-amber-500/10 p-2.5 text-amber-700">
                          Nothing is ticked and they aren&rsquo;t the named manager of any department, so they&rsquo;d open the app and see nobody at all. Tick a department, or set the reach to their own department or all departments.
                        </div>
                      )}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-sm">Can view</Label>
                          <Select value={form.manager_view_scope} onValueChange={(v) => setForm((f) => ({ ...f, manager_view_scope: v, manager_manage_scope: (['own','specific','all'].indexOf(v) < ['own','specific','all'].indexOf(f.manager_manage_scope)) ? v : f.manager_manage_scope }))} disabled={viewOnly}>
                            <SelectTrigger className="input-hive mt-1"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="own">Own department</SelectItem>
                              <SelectItem value="specific">Specific departments</SelectItem>
                              <SelectItem value="all">All departments</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-sm">Can manage</Label>
                          <Select value={form.manager_manage_scope} onValueChange={(v) => setForm({ ...form, manager_manage_scope: v })} disabled={viewOnly}>
                            <SelectTrigger className="input-hive mt-1"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="own">Own department</SelectItem>
                              <SelectItem value="specific" disabled={form.manager_view_scope === 'own'}>Specific departments</SelectItem>
                              <SelectItem value="all" disabled={form.manager_view_scope !== 'all'}>All departments</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      {(form.manager_view_scope === 'specific' || form.manager_manage_scope === 'specific') && (
                        <div className="p-3 bg-secondary/30 rounded-lg">
                          <Label className="text-sm font-medium">
                            {form.manager_view_scope === 'specific' && form.manager_manage_scope === 'specific'
                              ? 'Departments they can view & manage'
                              : form.manager_manage_scope === 'specific'
                                ? 'Departments they can manage'
                                : 'Departments they can view'}
                          </Label>
                          <p className="text-xs text-muted-foreground mb-2">
                            {form.manager_view_scope === 'specific' && form.manager_manage_scope === 'specific'
                              ? 'The departments this manager can see and act on.'
                              : form.manager_manage_scope === 'specific'
                                ? 'The departments this manager can act on (book / cancel / edit).'
                                : 'The departments this manager can see.'}
                          </p>
                          <div className="max-h-40 overflow-y-auto space-y-1 border border-border rounded-lg p-2 bg-background">
                            {departments.map((dept) => (
                              <label key={dept.id} className="flex items-center gap-2 p-1 hover:bg-secondary/50 rounded cursor-pointer">
                                <Checkbox checked={form.managed_department_ids?.includes(dept.id)} onCheckedChange={(checked) => { const cur = form.managed_department_ids || []; setForm({ ...form, managed_department_ids: checked ? [...cur, dept.id] : cur.filter((id) => id !== dept.id) }); }} disabled={viewOnly} />
                                <span className="text-sm">{dept.name}</span>
                                {/* Unticking this one changes nothing — they reach it as its named manager. */}
                                {(employee?.named_manager_department_ids || []).includes(dept.id) && (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-secondary text-muted-foreground">named manager</span>
                                )}
                              </label>
                            ))}
                            {departments.length === 0 && <p className="text-xs text-muted-foreground p-1">No departments yet.</p>}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/*
                    PAYROLL (DESIGN-payroll.md — "Who may run it").
                    ⚠️ NOT inside isMgrRole, and that is the whole point. The case is
                    Sandra in accounts: an ordinary employee who is the payroll clerk.
                    Restricting this to managers and admins excludes exactly the person
                    the feature exists for, and the workarounds are both bad — making
                    her a manager puts her in the org chart and approver dropdowns when
                    she manages nobody; making her an admin over-grants everything.
                    ⚠️ Payroll reach is its OWN axis and never touches manager reach.
                  */}
                  <div className="rounded-lg border border-border p-4 space-y-4">
                    <h4 className="font-medium flex items-center gap-2">
                      <Banknote className="w-4 h-4 text-amber-500" /> Payroll
                    </h4>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <Label className="font-medium">Can run payroll reports</Label>
                        <p className="text-xs text-muted-foreground mt-1">
                          Download absence data for a pay period. Every export is logged.
                        </p>
                      </div>
                      <Switch
                        checked={!!form.can_run_payroll}
                        onCheckedChange={(v) => setForm((f) => ({
                          ...f,
                          can_run_payroll: v,
                          // Default a manager to the reach they already have, so nothing
                          // widens by accident; anyone else has to be told explicitly.
                          payroll_scope: v
                            ? (f.payroll_scope && f.payroll_scope !== 'none'
                                ? f.payroll_scope
                                : (['manager', 'admin'].includes(f.role) ? 'inherit' : 'company'))
                            : 'none',
                        }))}
                        disabled={viewOnly || !isAdmin}
                        data-testid="payroll-permission-switch"
                      />
                    </div>

                    {form.can_run_payroll && (
                      <div className="space-y-3 pl-1">
                        <div>
                          <Label className="text-sm">Payroll covers</Label>
                          <Select
                            value={form.payroll_scope || 'company'}
                            onValueChange={(v) => setForm((f) => ({ ...f, payroll_scope: v }))}
                            disabled={viewOnly || !isAdmin}
                          >
                            <SelectTrigger className="mt-1" data-testid="payroll-scope-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {['manager', 'admin'].includes(form.role) && (
                                <SelectItem value="inherit">Same as the people they can already see</SelectItem>
                              )}
                              <SelectItem value="company">The whole company</SelectItem>
                              <SelectItem value="departments">Specific departments</SelectItem>
                              {locationsEnabled && <SelectItem value="locations">Specific locations</SelectItem>}
                              {/* Rob, 10/09/2026 — the bookkeeper who does only the
                                  directors, or a split that follows neither
                                  department nor site. */}
                              <SelectItem value="people">Specific people</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        {form.payroll_scope === 'departments' && (
                          <div className="max-h-40 overflow-y-auto space-y-1 border border-border rounded-lg p-2 bg-background">
                            {departments.map((dept) => (
                              <label key={dept.id} className="flex items-center gap-2 p-1 hover:bg-secondary/50 rounded cursor-pointer">
                                <Checkbox
                                  checked={form.payroll_scope_department_ids?.includes(dept.id)}
                                  onCheckedChange={(checked) => {
                                    const cur = form.payroll_scope_department_ids || [];
                                    setForm({ ...form, payroll_scope_department_ids: checked ? [...cur, dept.id] : cur.filter((id) => id !== dept.id) });
                                  }}
                                  disabled={viewOnly || !isAdmin}
                                />
                                <span className="text-sm">{dept.name}</span>
                              </label>
                            ))}
                            {departments.length === 0 && <p className="text-xs text-muted-foreground p-1">No departments yet.</p>}
                          </div>
                        )}

                        {form.payroll_scope === 'locations' && (
                          <div className="max-h-40 overflow-y-auto space-y-1 border border-border rounded-lg p-2 bg-background">
                            {(locations || []).map((loc) => (
                              <label key={loc.id} className="flex items-center gap-2 p-1 hover:bg-secondary/50 rounded cursor-pointer">
                                <Checkbox
                                  checked={form.payroll_scope_location_ids?.includes(loc.id)}
                                  onCheckedChange={(checked) => {
                                    const cur = form.payroll_scope_location_ids || [];
                                    setForm({ ...form, payroll_scope_location_ids: checked ? [...cur, loc.id] : cur.filter((id) => id !== loc.id) });
                                  }}
                                  disabled={viewOnly || !isAdmin}
                                />
                                <span className="text-sm">{loc.name}</span>
                              </label>
                            ))}
                            {(locations || []).length === 0 && <p className="text-xs text-muted-foreground p-1">No locations yet.</p>}
                          </div>
                        )}

                        {form.payroll_scope === 'people' && (
                          <div className="max-h-40 overflow-y-auto space-y-1 border border-border rounded-lg p-2 bg-background">
                            {(people || [])
                              .filter((p) => p.id !== employee?.id)
                              .map((p) => (
                                <label key={p.id} className="flex items-center gap-2 p-1 hover:bg-secondary/50 rounded cursor-pointer">
                                  <Checkbox
                                    checked={form.payroll_scope_user_ids?.includes(p.id)}
                                    onCheckedChange={(checked) => {
                                      const cur = form.payroll_scope_user_ids || [];
                                      setForm({ ...form, payroll_scope_user_ids: checked ? [...cur, p.id] : cur.filter((id) => id !== p.id) });
                                    }}
                                    disabled={viewOnly || !isAdmin}
                                  />
                                  <span className="text-sm">
                                    {`${p.first_name || ''} ${p.last_name || ''}`.trim() || p.email}
                                  </span>
                                </label>
                              ))}
                            {(people || []).length === 0 && <p className="text-xs text-muted-foreground p-1">No people to choose from.</p>}
                          </div>
                        )}

                        {/*
                          ⚠️ GENERATED, NEVER TYPED. The whole safety argument for letting
                          an ordinary employee export absence data is that whoever grants
                          it is told, right here, exactly whose data it covers.
                        */}
                        <div className="rounded-lg border border-border bg-secondary/30 px-3 py-2">
                          <p className="text-xs text-muted-foreground">
                            {form.first_name || 'They'} will be able to export absence data for{' '}
                            <span className="font-medium text-foreground">
                              {payrollCoversText(form, departments, locations, people)}
                            </span>.
                          </p>
                          {employee?.payroll_people != null && (
                            <p className="text-[11px] text-muted-foreground mt-1">
                              Last saved: {employee.payroll_people} {employee.payroll_people === 1 ? 'person' : 'people'}.
                            </p>
                          )}
                        </div>

                        {!isAdmin && (
                          <p className="text-xs text-muted-foreground">Only an admin can change payroll permissions.</p>
                        )}
                      </div>
                    )}
                  </div>

                  {isMgrRole && (
                    <div className="rounded-lg border border-border p-4 space-y-4">
                      <h4 className="font-medium flex items-center gap-2"><UserCog className="w-4 h-4 text-indigo-500" /> Manager permissions</h4>
                      <p className="text-sm text-muted-foreground">What this {form.role === 'admin' ? 'admin' : 'manager'} can do with their team. By default these inherit the global settings in Settings → Manager Permissions.</p>
                      <div className="p-4 rounded-lg border border-border bg-secondary/20">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <Label className="font-medium">Use global manager settings</Label>
                            <p className="text-xs text-muted-foreground mt-1">When on, this person uses the company-wide defaults. Turn off to customise their permissions.</p>
                          </div>
                          <div className="flex items-center gap-3">
                            <Popover onOpenChange={(o) => { if (o) fetchGlobalManagerSettings(); }}>
                              <PopoverTrigger asChild>
                                <Button variant="outline" size="sm" className="gap-1" data-nav="true"><Eye className="w-4 h-4" />View defaults</Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-80 p-0" align="end">
                                <div className="p-3 border-b border-border bg-secondary/30"><h4 className="font-semibold text-sm">Global manager defaults</h4><p className="text-xs text-muted-foreground">Current company-wide settings</p></div>
                                <div className="p-3 space-y-2 max-h-64 overflow-y-auto">
                                  {loadingGlobalSettings ? (
                                    <div className="flex items-center justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
                                  ) : globalManagerSettings ? (
                                    <>
                                      {[
                                        { key: 'mgr_can_view_team_balances', label: 'View team balances' },
                                        { key: 'mgr_can_approve_leave', label: 'Approve/decline leave' },
                                        { key: 'mgr_can_view_leave_reasons', label: 'View leave reasons' },
                                        { key: 'mgr_can_edit_team_details', label: 'Edit team details' },
                                        { key: 'mgr_can_view_personal_info', label: 'View personal info' },
                                        { key: 'mgr_can_cancel_approved_leave', label: 'Cancel approved leave' },
                                        { key: 'mgr_can_book_team_leave', label: 'Book leave for team' },
                                        { key: 'mgr_can_approve_own_leave', label: 'Approve own leave' },
                                        { key: 'mgr_receive_notifications', label: 'Email notifications' },
                                        { key: 'mgr_can_create_blackouts', label: 'Add no-leave dates' },
                                        { key: 'mgr_can_view_reports', label: 'View reports' },
                                        { key: 'mgr_can_view_sickness', label: 'View sickness' },
                                      ].map((perm) => (
                                        <div key={perm.key} className="flex items-center justify-between py-1">
                                          <span className="text-sm">{perm.label}</span>
                                          {globalManagerSettings[perm.key] ? (<CheckCircle className="w-4 h-4 text-green-500" />) : (<XCircle className="w-4 h-4 text-red-400" />)}
                                        </div>
                                      ))}
                                    </>
                                  ) : (
                                    <p className="text-sm text-muted-foreground py-2">Unable to load settings</p>
                                  )}
                                </div>
                              </PopoverContent>
                            </Popover>
                            <Switch checked={form.use_global_manager_settings} onCheckedChange={(checked) => setForm({ ...form, use_global_manager_settings: checked })} disabled={viewOnly} />
                          </div>
                        </div>
                      </div>
                      {!form.use_global_manager_settings && (
                        <div className="space-y-3 pt-1">
                          <p className="text-sm font-medium text-amber-500">Custom permissions active</p>
                          {[
                            { key: 'mgr_override_can_view_team_balances', label: 'View team leave balances', desc: 'Managers can see their team\'s remaining leave on the Team calendar and Employees page. Whoever approves a request always sees the person\'s balance when they come to approve it.' },
                            { key: 'mgr_override_can_approve_leave', label: 'Approve/decline leave', desc: 'Approve or decline leave from their team' },
                            { key: 'mgr_override_can_approve_own_leave', label: 'Approve own leave', desc: 'Sign off their own leave without routing to another approver' },
                            { key: 'mgr_override_can_view_leave_reasons', label: 'View leave reasons', desc: 'See the reason given for leave requests' },
                            { key: 'mgr_override_can_edit_team_details', label: 'Edit team member details', desc: "Edit their team members' information" },
                            { key: 'mgr_override_can_view_personal_info', label: 'View personal info', desc: 'See phone, DOB, emergency contacts' },
                            { key: 'mgr_override_can_cancel_approved_leave', label: 'Cancel approved leave', desc: 'Cancel leave already approved for their team' },
                            { key: 'mgr_override_can_book_team_leave', label: 'Book leave for team', desc: 'Book or record leave for their team from the team calendar' },
                            { key: 'mgr_override_receive_notifications', label: 'Email notifications', desc: 'Email them when their team requests leave. Overdue reminders are always sent' },
                            { key: 'mgr_override_can_create_blackouts', label: 'Add no-leave dates', desc: 'Block dates for leave in their department' },
                            { key: 'mgr_override_can_view_reports', label: 'View reports', desc: 'Open Reports, showing only the people inside their reach' },
                            { key: 'mgr_override_can_view_sickness', label: 'View sickness', desc: 'Open Reports → Sickness for their reach. Health information: needs View reports too' },
                          ].map((perm) => (
                            <div key={perm.key} className="flex items-center justify-between p-3 rounded-lg bg-secondary/30">
                              <div><Label className="font-medium">{perm.label}</Label><p className="text-xs text-muted-foreground">{perm.desc}</p></div>
                              <Switch checked={form[perm.key]} onCheckedChange={(checked) => setForm({ ...form, [perm.key]: checked })} disabled={viewOnly} />
                            </div>
                          ))}
                          <div className="p-3 rounded-lg bg-secondary/30">
                            <Label className="font-medium">Max days can approve</Label>
                            <p className="text-xs text-muted-foreground mb-2">Maximum consecutive days this manager can approve (0 = unlimited)</p>
                            <input type="number" min="0" max="365" value={form.mgr_override_max_approval_days} onChange={(e) => setForm({ ...form, mgr_override_max_approval_days: parseInt(e.target.value) || 0 })} className="w-20 px-3 py-1 rounded border border-input bg-background text-sm" />
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                </div>
              )}

            </div>
          </div>

          <div className="flex items-center justify-between gap-3 pt-5 mt-2 border-t border-border">
            <Button type="button" variant="ghost" data-nav="true" onClick={() => onOpenChange(false)}>Cancel</Button>
            <div className="flex items-center gap-2">
              {cur > 0 && (<Button type="button" variant="outline" data-nav="true" onClick={() => setStep(cur - 1)}>Back</Button>)}
              {cur < steps.length - 1 && (<Button type="button" variant="outline" data-nav="true" onClick={() => setStep(cur + 1)}>Next</Button>)}
              {!viewOnly && (
                <button type="submit" data-nav="true" disabled={loading} className={`px-4 py-2 rounded-md text-white text-sm font-medium flex items-center ${ac.btn} disabled:opacity-60`} data-testid="save-employee-btn">
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  {isEditing ? 'Save changes' : 'Add employee'}
                </button>
              )}
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddEditEmployeeDialog;
